import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Lock, LogOut, Plus, Edit2, Trash2, Briefcase, Newspaper, 
  Save, X, Users, Eye, EyeOff, ChevronDown, ChevronUp, Languages, FileText, UserCircle, Phone, Mail, Truck, Settings, Clock, Inbox, SettingsIcon, HelpCircle, BarChart3, Globe, MapPin, Shield, Home, Star, MessageSquare, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { ImageUpload } from '../components/ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api`;

// Login Component
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Set page title for admin
  usePageTitle('admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('adminAuth', 'true');
      onLogin();
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white rounded-3xl shadow-xl">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#C41E3A]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 text-sm mt-2">Enter your password to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-600">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-slate-50 border-slate-200 rounded-xl"
                placeholder="Enter admin password"
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full py-6"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Careers Management
function CareersManager() {
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPosition, setEditingPosition] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'CAREERS', hero_subtitle_bg: 'КАРИЕРИ',
    hero_title_en: 'Join Our', hero_title_bg: 'Присъединете се към нашия',
    hero_title_highlight_en: 'Team', hero_title_highlight_bg: 'екип',
    hero_description_en: 'Build your career with a leading European transport company.',
    hero_description_bg: 'Изградете кариерата си с водеща европейска транспортна компания.',
    positions_subtitle_en: 'OPEN POSITIONS', positions_subtitle_bg: 'ОТВОРЕНИ ПОЗИЦИИ',
    positions_title_en: 'Current', positions_title_bg: 'Текущи',
    positions_title_highlight_en: 'Opportunities', positions_title_highlight_bg: 'възможности',
    no_positions_en: 'No open positions at the moment.', no_positions_bg: 'Няма отворени позиции в момента.',
    cta_title_en: 'Don\'t See a Matching Position?', cta_title_bg: 'Не намирате подходяща позиция?',
    cta_text_en: 'Send us your CV!',
    cta_text_bg: 'Изпратете ни вашето CV!',
    cta_button_en: 'Contact HR', cta_button_bg: 'Свържете се с HR'
  });
  const [formData, setFormData] = useState({
    title_en: '', title_bg: '', department: '', location: '', type: 'Full-time',
    description_en: '', description_bg: '', 
    requirements_en: '', requirements_bg: '', 
    benefits_en: '', benefits_bg: '', 
    is_active: true
  });

  const fetchData = async () => {
    try {
      const [posRes, appRes] = await Promise.all([
        axios.get(`${API}/admin/careers`),
        axios.get(`${API}/admin/applications`)
      ]);
      setPositions(posRes.data.positions || []);
      setApplications(appRes.data.applications || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/careers-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/careers-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  useEffect(() => { fetchData(); fetchPageContent(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        requirements_en: formData.requirements_en.split('\n').filter(r => r.trim()),
        requirements_bg: formData.requirements_bg.split('\n').filter(r => r.trim()),
        benefits_en: formData.benefits_en.split('\n').filter(b => b.trim()),
        benefits_bg: formData.benefits_bg.split('\n').filter(b => b.trim())
      };
      
      if (editingPosition) {
        await axios.put(`${API}/admin/careers/${editingPosition.id}`, data);
        toast.success('Position updated!');
      } else {
        await axios.post(`${API}/admin/careers`, data);
        toast.success('Position created!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save position');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this position?')) return;
    try {
      await axios.delete(`${API}/admin/careers/${id}`);
      toast.success('Position deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete position');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPosition(null);
    setFormData({ 
      title_en: '', title_bg: '', department: '', location: '', type: 'Full-time', 
      description_en: '', description_bg: '', 
      requirements_en: '', requirements_bg: '', 
      benefits_en: '', benefits_bg: '', 
      is_active: true 
    });
  };

  const startEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      ...position,
      requirements_en: position.requirements_en?.join('\n') || '',
      requirements_bg: position.requirements_bg?.join('\n') || '',
      benefits_en: position.benefits_en?.join('\n') || '',
      benefits_bg: position.benefits_bg?.join('\n') || ''
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Careers Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
                <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} placeholder="CTA Title EN" />
                <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} placeholder="CTA Title BG" />
                <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} placeholder="Button EN" />
                <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} placeholder="Button BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Career Positions</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'Add Position'}
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-slate-100 rounded-3xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Common Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Department *</Label>
                  <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="mt-1 rounded-xl" required />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="mt-1 rounded-xl" required />
                </div>
                <div>
                  <Label>Employment Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                    <span className="text-sm text-slate-600">Active (visible)</span>
                  </label>
                </div>
              </div>

              {/* Bilingual Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* English */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800 flex items-center gap-2">🇬🇧 English</h4>
                  <div>
                    <Label>Job Title (EN) *</Label>
                    <Input value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} className="mt-1 rounded-xl bg-white" required />
                  </div>
                  <div>
                    <Label>Description (EN) *</Label>
                    <Textarea value={formData.description_en} onChange={(e) => setFormData({...formData, description_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} required />
                  </div>
                  <div>
                    <Label>Requirements (EN) - one per line</Label>
                    <Textarea value={formData.requirements_en} onChange={(e) => setFormData({...formData, requirements_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="3+ years experience&#10;Valid driving license" />
                  </div>
                  <div>
                    <Label>Benefits (EN) - one per line</Label>
                    <Textarea value={formData.benefits_en} onChange={(e) => setFormData({...formData, benefits_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="Competitive salary&#10;Health insurance" />
                  </div>
                </div>

                {/* Bulgarian */}
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800 flex items-center gap-2">🇧🇬 Български</h4>
                  <div>
                    <Label>Заглавие (BG)</Label>
                    <Input value={formData.title_bg} onChange={(e) => setFormData({...formData, title_bg: e.target.value})} className="mt-1 rounded-xl bg-white" />
                  </div>
                  <div>
                    <Label>Описание (BG)</Label>
                    <Textarea value={formData.description_bg} onChange={(e) => setFormData({...formData, description_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} />
                  </div>
                  <div>
                    <Label>Изисквания (BG) - по едно на ред</Label>
                    <Textarea value={formData.requirements_bg} onChange={(e) => setFormData({...formData, requirements_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="3+ години опит&#10;Валидна шофьорска книжка" />
                  </div>
                  <div>
                    <Label>Предимства (BG) - по едно на ред</Label>
                    <Textarea value={formData.benefits_bg} onChange={(e) => setFormData({...formData, benefits_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="Конкурентна заплата&#10;Здравно осигуряване" />
                  </div>
                </div>
              </div>

              <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                <Save className="w-4 h-4 mr-2" /> {editingPosition ? 'Update Position' : 'Create Position'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Positions List */}
      <div className="space-y-4">
        {positions.map((position) => (
          <Card key={position.id} className="bg-white border-slate-100 rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${position.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900">{position.title_en || position.title}</h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                      <span>📍 {position.location_en || position.location}</span>
                      <span>•</span>
                      <span>⏱️ {position.type_en || position.type}</span>
                      {position.department && <><span>•</span><span>🏢 {position.department}</span></>}
                    </div>
                    {position.title_bg && <p className="text-xs text-green-600 mt-1">BG: {position.title_bg}</p>}
                    {(position.description_en || position.description) && (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{position.description_en || position.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => startEdit(position)} className="rounded-full">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(position.id)} className="rounded-full text-red-500 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Applications */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Applications ({applications.length})</h3>
        {applications.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No applications yet</p>
        ) : (
          <div className="space-y-3">
            {applications.map((app) => (
              <Card key={app.id} className="bg-slate-50 border-slate-100 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-slate-900">{app.name}</h4>
                      <p className="text-sm text-slate-500">{app.email} • {app.phone}</p>
                      {app.cover_letter && <p className="text-sm text-slate-600 mt-2">{app.cover_letter}</p>}
                    </div>
                    <span className="text-xs text-slate-400">{new Date(app.created_at).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// News Management
function NewsManager() {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'NEWS & UPDATES', hero_subtitle_bg: 'НОВИНИ И АКТУАЛИЗАЦИИ',
    hero_title_en: 'Latest', hero_title_bg: 'Последни',
    hero_title_highlight_en: 'News', hero_title_highlight_bg: 'новини',
    hero_description_en: 'Stay updated with the latest news, company updates, and industry insights from Tetralog.',
    hero_description_bg: 'Бъдете в крак с последните новини, актуализации от компанията и индустриални прозрения от Tetralog.',
    no_news_title_en: 'No News Yet', no_news_title_bg: 'Все още няма новини',
    no_news_text_en: 'Check back soon for the latest updates from Tetralog!',
    no_news_text_bg: 'Скоро ще има актуализации от Tetralog!',
    cta_title_en: 'Stay Connected', cta_title_bg: 'Останете свързани',
    cta_text_en: 'Follow us on social media for the latest updates and news.',
    cta_text_bg: 'Следете ни в социалните мрежи за последните актуализации и новини.',
    cta_button_en: 'Contact Us', cta_button_bg: 'Свържете се с нас'
  });
  const [formData, setFormData] = useState({
    title_en: '', title_bg: '', 
    excerpt_en: '', excerpt_bg: '', 
    content_en: '', content_bg: '', 
    category: 'Company News', image_url: '', is_published: true
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/news`);
      setArticles(res.data.articles);
    } catch (error) {
      toast.error('Failed to load news');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/news-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/news-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  useEffect(() => { fetchData(); fetchPageContent(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/news/${editingArticle.id}`, formData);
        toast.success('Article updated!');
      } else {
        await axios.post(`${API}/admin/news`, formData);
        toast.success('Article created!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`);
      toast.success('Article deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingArticle(null);
    setFormData({ 
      title_en: '', title_bg: '', 
      excerpt_en: '', excerpt_bg: '', 
      content_en: '', content_bg: '', 
      category: 'Company News', image_url: '', is_published: true 
    });
  };

  const startEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title_en: article.title_en || article.title || '',
      title_bg: article.title_bg || '',
      excerpt_en: article.excerpt_en || article.excerpt || '',
      excerpt_bg: article.excerpt_bg || '',
      content_en: article.content_en || article.content || '',
      content_bg: article.content_bg || '',
      category: article.category,
      image_url: article.image_url || '',
      is_published: article.is_published
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">News Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
                <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} placeholder="CTA Title EN" />
                <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} placeholder="CTA Title BG" />
                <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} placeholder="CTA Button EN" />
                <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} placeholder="CTA Button BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">News Articles</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'Add Article'}
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-slate-100 rounded-3xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Common Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Company News">Company News</SelectItem>
                      <SelectItem value="Industry Updates">Industry Updates</SelectItem>
                      <SelectItem value="Press Release">Press Release</SelectItem>
                      <SelectItem value="Events">Events</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload 
                      value={formData.image_url} 
                      onChange={(url) => setFormData({...formData, image_url: url})} 
                      placeholder="Enter URL or upload image"
                    />
                  </div>
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({...formData, is_published: e.target.checked})} className="rounded" />
                    <span className="text-sm text-slate-600">Published (visible)</span>
                  </label>
                </div>
              </div>

              {/* Bilingual Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* English */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800 flex items-center gap-2">🇬🇧 English</h4>
                  <div>
                    <Label>Title (EN) *</Label>
                    <Input value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} className="mt-1 rounded-xl bg-white" required />
                  </div>
                  <div>
                    <Label>Excerpt (EN) *</Label>
                    <Textarea value={formData.excerpt_en} onChange={(e) => setFormData({...formData, excerpt_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={2} required placeholder="Short summary for preview..." />
                  </div>
                  <div>
                    <Label>Content (EN) *</Label>
                    <Textarea value={formData.content_en} onChange={(e) => setFormData({...formData, content_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={6} required placeholder="Full article content..." />
                  </div>
                </div>

                {/* Bulgarian */}
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800 flex items-center gap-2">🇧🇬 Български</h4>
                  <div>
                    <Label>Заглавие (BG)</Label>
                    <Input value={formData.title_bg} onChange={(e) => setFormData({...formData, title_bg: e.target.value})} className="mt-1 rounded-xl bg-white" />
                  </div>
                  <div>
                    <Label>Резюме (BG)</Label>
                    <Textarea value={formData.excerpt_bg} onChange={(e) => setFormData({...formData, excerpt_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={2} placeholder="Кратко резюме..." />
                  </div>
                  <div>
                    <Label>Съдържание (BG)</Label>
                    <Textarea value={formData.content_bg} onChange={(e) => setFormData({...formData, content_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={6} placeholder="Пълен текст на статията..." />
                  </div>
                </div>
              </div>

              <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                <Save className="w-4 h-4 mr-2" /> {editingArticle ? 'Update Article' : 'Create Article'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Articles List */}
      <div className="space-y-4">
        {articles.map((article) => (
          <Card key={article.id} className="bg-white border-slate-100 rounded-2xl">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${article.is_published ? 'bg-green-500' : 'bg-slate-300'}`} />
                <div>
                  <h3 className="font-bold text-slate-900">{article.title_en || article.title}</h3>
                  <p className="text-sm text-slate-500">{article.category} • {article.date || (article.created_at ? new Date(article.created_at).toLocaleDateString() : 'No date')}</p>
                  {article.title_bg && <p className="text-xs text-green-600">BG: {article.title_bg}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(article)} className="rounded-full">
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(article.id)} className="rounded-full text-red-500 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {articles.length === 0 && <p className="text-slate-500 text-center py-8">No articles yet</p>}
      </div>
    </div>
  );
}

// Site Content / Translations Manager
function SiteContentManager() {
  const [content, setContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-content`);
      setContent(res.data.content);
    } catch (error) {
      toast.error('Failed to load site content');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchContent(); }, []);

  const handleChange = (key, field, value) => {
    setContent(prev => prev.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, { content });
      toast.success('Site content saved successfully!');
    } catch (error) {
      toast.error('Failed to save site content');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredContent = content.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group content by section - Only show footer and cookies (other sections are managed separately)
  const allowedSections = ['footer', 'cookies'];
  const groupedContent = filteredContent.reduce((acc, item) => {
    const section = item.key.split('.')[0];
    if (!allowedSections.includes(section)) return acc;
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  const sectionLabels = {
    footer: '📋 Footer',
    cookies: '🍪 Cookie Consent'
  };

  if (isLoading) {
    return <div className="text-center py-12"><div className="spinner mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Site Content</h2>
          <p className="text-slate-500 text-sm">Edit all static text in English and Bulgarian</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Search */}
      <div>
        <Input 
          placeholder="Search content..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md rounded-full"
        />
      </div>

      {/* Content Sections */}
      <div className="space-y-8">
        {Object.entries(groupedContent).map(([section, items]) => (
          <Card key={section} className="bg-white border-slate-100 rounded-3xl">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                {sectionLabels[section] || section}
              </h3>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.key} className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-2xl">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">{item.label}</Label>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">EN</span>
                      </div>
                      {item.value_en && item.value_en.length > 100 ? (
                        <Textarea 
                          value={item.value_en}
                          onChange={(e) => handleChange(item.key, 'value_en', e.target.value)}
                          className="bg-white rounded-xl text-sm"
                          rows={3}
                        />
                      ) : (
                        <Input 
                          value={item.value_en}
                          onChange={(e) => handleChange(item.key, 'value_en', e.target.value)}
                          className="bg-white rounded-xl text-sm"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">&nbsp;</Label>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">BG</span>
                      </div>
                      {item.value_bg && item.value_bg.length > 100 ? (
                        <Textarea 
                          value={item.value_bg}
                          onChange={(e) => handleChange(item.key, 'value_bg', e.target.value)}
                          className="bg-white rounded-xl text-sm"
                          rows={3}
                        />
                      ) : (
                        <Input 
                          value={item.value_bg}
                          onChange={(e) => handleChange(item.key, 'value_bg', e.target.value)}
                          className="bg-white rounded-xl text-sm"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Save Button at bottom */}
      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full px-8">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
}

// Contact Persons Manager
function ContactPersonsManager() {
  const [persons, setPersons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPerson, setEditingPerson] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'CONTACT US', hero_subtitle_bg: 'КОНТАКТ',
    hero_title_en: 'Get in', hero_title_bg: 'Свържете се',
    hero_title_highlight_en: 'Touch', hero_title_highlight_bg: 'с нас',
    hero_description_en: 'Have a question or need a quote? Our team is ready to help you with all your transport and logistics needs.',
    hero_description_bg: 'Имате въпрос или се нуждаете от оферта? Нашият екип е готов да ви помогне.',
    call_us_en: 'Call Us', call_us_bg: 'Обадете се',
    email_us_en: 'Email Us', email_us_bg: 'Пишете ни',
    working_hours_en: 'Working Hours', working_hours_bg: 'Работно време',
    mon_fri_en: 'Mon-Fri: 08:00-17:30', mon_fri_bg: 'Пон-Пет: 08:00-17:30',
    contact_tab_en: 'Contact', contact_tab_bg: 'Контакт',
    quote_tab_en: 'Get Quote', quote_tab_bg: 'Оферта',
    send_message_en: 'Send Us a Message', send_message_bg: 'Изпратете съобщение',
    send_en: 'Send Message', send_bg: 'Изпрати съобщение',
    request_quote_en: 'Request a Quote', request_quote_bg: 'Заявете оферта',
    submit_quote_en: 'Submit Quote Request', submit_quote_bg: 'Изпрати заявка',
    our_location_en: 'Our Location', our_location_bg: 'Нашето местоположение'
  });
  const [formData, setFormData] = useState({
    name_en: '', name_bg: '', position_en: '', position_bg: '',
    email: '', phone: '', image_url: '', order: 0, is_active: true
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/contact-persons`);
      setPersons(res.data.persons);
    } catch (error) {
      toast.error('Failed to load contact persons');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/contact-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/contact-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  useEffect(() => { fetchData(); fetchPageContent(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPerson) {
        await axios.put(`${API}/admin/contact-persons/${editingPerson.id}`, formData);
        toast.success('Contact person updated!');
      } else {
        await axios.post(`${API}/admin/contact-persons`, formData);
        toast.success('Contact person created!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save contact person');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact person?')) return;
    try {
      await axios.delete(`${API}/admin/contact-persons/${id}`);
      toast.success('Contact person deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact person');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPerson(null);
    setFormData({ name_en: '', name_bg: '', position_en: '', position_bg: '', email: '', phone: '', image_url: '', order: 0, is_active: true });
  };

  const startEdit = (person) => {
    setEditingPerson(person);
    setFormData(person);
    setShowForm(true);
  };

  if (isLoading) {
    return <div className="text-center py-12"><div className="spinner mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Contact Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <h4 className="font-bold text-slate-700">Hero Section</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
              </div>
              <h4 className="font-bold text-slate-700 mt-4">Labels</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.call_us_en} onChange={(e) => setPageContent({...pageContent, call_us_en: e.target.value})} placeholder="Call Us EN" />
                <Input value={pageContent.call_us_bg} onChange={(e) => setPageContent({...pageContent, call_us_bg: e.target.value})} placeholder="Call Us BG" />
                <Input value={pageContent.email_us_en} onChange={(e) => setPageContent({...pageContent, email_us_en: e.target.value})} placeholder="Email Us EN" />
                <Input value={pageContent.email_us_bg} onChange={(e) => setPageContent({...pageContent, email_us_bg: e.target.value})} placeholder="Email Us BG" />
                <Input value={pageContent.working_hours_en} onChange={(e) => setPageContent({...pageContent, working_hours_en: e.target.value})} placeholder="Working Hours EN" />
                <Input value={pageContent.working_hours_bg} onChange={(e) => setPageContent({...pageContent, working_hours_bg: e.target.value})} placeholder="Working Hours BG" />
                <Input value={pageContent.send_en} onChange={(e) => setPageContent({...pageContent, send_en: e.target.value})} placeholder="Send Button EN" />
                <Input value={pageContent.send_bg} onChange={(e) => setPageContent({...pageContent, send_bg: e.target.value})} placeholder="Send Button BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Contact Persons</h2>
          <p className="text-slate-500 text-sm">Manage team members shown on the contact page</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Person
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-slate-100 rounded-3xl">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{editingPerson ? 'Edit Contact Person' : 'Add Contact Person'}</h3>
              <Button variant="ghost" size="sm" onClick={resetForm}><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bilingual Names */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800">🇬🇧 English</h4>
                  <div>
                    <Label>Name (EN) *</Label>
                    <Input value={formData.name_en} onChange={(e) => setFormData({...formData, name_en: e.target.value})} className="mt-1 rounded-xl bg-white" required />
                  </div>
                  <div>
                    <Label>Position (EN) *</Label>
                    <Input value={formData.position_en} onChange={(e) => setFormData({...formData, position_en: e.target.value})} className="mt-1 rounded-xl bg-white" required placeholder="e.g., Sales Manager" />
                  </div>
                </div>
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800">🇧🇬 Български</h4>
                  <div>
                    <Label>Име (BG)</Label>
                    <Input value={formData.name_bg} onChange={(e) => setFormData({...formData, name_bg: e.target.value})} className="mt-1 rounded-xl bg-white" />
                  </div>
                  <div>
                    <Label>Позиция (BG)</Label>
                    <Input value={formData.position_bg} onChange={(e) => setFormData({...formData, position_bg: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="напр., Мениджър продажби" />
                  </div>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="mt-1 rounded-xl" required />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="mt-1 rounded-xl" required placeholder="+359..." />
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                    <span className="text-sm text-slate-600">Active (visible)</span>
                  </label>
                </div>
              </div>
              
              <div>
                <Label>Photo</Label>
                <div className="mt-1">
                  <ImageUpload 
                    value={formData.image_url} 
                    onChange={(url) => setFormData({...formData, image_url: url})} 
                    placeholder="Enter URL or upload photo"
                  />
                </div>
              </div>

              <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                <Save className="w-4 h-4 mr-2" /> {editingPerson ? 'Update Person' : 'Add Person'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Persons List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {persons.map((person) => (
          <Card key={person.id} className="bg-white border-slate-100 rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 min-w-[3.5rem] rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {person.image_url ? (
                    <img src={person.image_url} alt={person.name_en} className="w-full h-full object-cover" />
                  ) : (
                    <UserCircle className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 truncate">{person.name_en}</h3>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${person.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  </div>
                  <p className="text-sm text-[#C41E3A] truncate">{person.position_en}</p>
                  {person.name_bg && <p className="text-xs text-green-600 truncate">BG: {person.name_bg}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" />{person.email}</span>
                    <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 flex-shrink-0" />{person.phone}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => startEdit(person)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(person.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {persons.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No contact persons yet. Add your first team member!</p>}
      </div>
    </div>
  );
}

// Fleet Management
function FleetManager() {
  const [vehicles, setVehicles] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'OUR FLEET',
    hero_subtitle_bg: 'НАШИЯТ ФЛОТ',
    hero_title_en: 'Modern',
    hero_title_bg: 'Модерен',
    hero_title_highlight_en: 'EURO 6',
    hero_title_highlight_bg: 'EURO 6',
    hero_title_end_en: 'Fleet',
    hero_title_end_bg: 'флот',
    hero_description_en: 'Our diverse fleet covers cargo volumes from 11 m³ to 121 m³, equipped with the latest technology and meeting the highest environmental standards.',
    hero_description_bg: 'Нашият разнообразен флот покрива обеми на товари от 11 m³ до 121 m³, оборудван с най-новите технологии и отговарящ на най-високите екологични стандарти.',
    features_en: [
      'EURO 6 emission standards on all vehicles',
      'ECO zone access certification for all Western European cities',
      'Tail lifts and pump trucks for flexible loading',
      'GPS tracking on every vehicle',
      'Regular maintenance and safety inspections',
      'Professional trained drivers'
    ],
    features_bg: [
      'EURO 6 емисионни стандарти на всички превозни средства',
      'ECO зона достъп сертификат за всички западноевропейски градове',
      'Хидравлични платформи и електрокари за гъвкаво товарене',
      'GPS проследяване на всяко превозно средство',
      'Редовна поддръжка и проверки за безопасност',
      'Професионално обучени шофьори'
    ],
    grid_subtitle_en: 'VEHICLE RANGE',
    grid_subtitle_bg: 'ГАМА ПРЕВОЗНИ СРЕДСТВА',
    grid_title_en: 'Choose the Right',
    grid_title_bg: 'Изберете правилното',
    grid_title_highlight_en: 'Vehicle',
    grid_title_highlight_bg: 'превозно средство',
    cta_title_en: 'Not Sure Which Vehicle You Need?',
    cta_title_bg: 'Не сте сигурни кое превозно средство ви трябва?',
    cta_text_en: 'Our logistics experts will help you choose the perfect solution for your cargo.',
    cta_text_bg: 'Нашите логистични експерти ще ви помогнат да изберете перфектното решение за вашия товар.',
    cta_button_en: 'Get Expert Advice',
    cta_button_bg: 'Получете експертен съвет'
  });
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [showPageContent, setShowPageContent] = useState(false);
  
  const defaultForm = {
    name: '',
    type_en: '',
    type_bg: '',
    capacity_m3: '',
    payload_kg: '',
    euro_pallets: '',
    dimensions: '',
    eco_standard: 'EURO 6',
    image_url: '',
    order: 0,
    is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    fetchVehicles();
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/fleet-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching fleet page content:', error);
    }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/fleet-page-content`, pageContent);
      toast.success('Fleet page content saved!');
    } catch (error) {
      toast.error('Error saving page content');
    } finally {
      setIsSavingContent(false);
    }
  };

  const updateFeature = (lang, index, value) => {
    const field = `features_${lang}`;
    const newFeatures = [...pageContent[field]];
    newFeatures[index] = value;
    setPageContent({ ...pageContent, [field]: newFeatures });
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/admin/fleet`);
      setVehicles(response.data.fleet || []);
    } catch (error) {
      console.error('Error fetching fleet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await axios.put(`${API}/admin/fleet/${editingVehicle.id}`, formData);
        toast.success('Vehicle updated!');
      } else {
        await axios.post(`${API}/admin/fleet`, formData);
        toast.success('Vehicle added!');
      }
      fetchVehicles();
      setIsFormOpen(false);
      setEditingVehicle(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await axios.delete(`${API}/admin/fleet/${id}`);
      toast.success('Vehicle deleted!');
      fetchVehicles();
    } catch (error) {
      toast.error('Error deleting vehicle');
    }
  };

  const startEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name || '',
      type_en: vehicle.type_en || vehicle.type || '',
      type_bg: vehicle.type_bg || '',
      capacity_m3: vehicle.capacity_m3 || '',
      payload_kg: vehicle.payload_kg || '',
      euro_pallets: vehicle.euro_pallets || '',
      dimensions: vehicle.dimensions || '',
      eco_standard: vehicle.eco_standard || 'EURO 6',
      image_url: vehicle.image_url || '',
      order: vehicle.order || 0,
      is_active: vehicle.is_active !== false
    });
    setIsFormOpen(true);
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button 
            onClick={() => setShowPageContent(!showPageContent)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Page Content (Titles, Descriptions)</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              {/* Hero Section */}
              <h4 className="font-bold text-slate-700">Hero Section</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Subtitle (EN)</Label>
                  <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Subtitle (BG)</Label>
                  <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title Start (EN)</Label>
                  <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} className="mt-1" placeholder="Modern" />
                </div>
                <div>
                  <Label className="text-xs">Title Start (BG)</Label>
                  <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} className="mt-1" placeholder="Модерен" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title Highlight (EN) - colored</Label>
                  <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} className="mt-1" placeholder="EURO 6" />
                </div>
                <div>
                  <Label className="text-xs">Title Highlight (BG) - colored</Label>
                  <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} className="mt-1" placeholder="EURO 6" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title End (EN)</Label>
                  <Input value={pageContent.hero_title_end_en} onChange={(e) => setPageContent({...pageContent, hero_title_end_en: e.target.value})} className="mt-1" placeholder="Fleet" />
                </div>
                <div>
                  <Label className="text-xs">Title End (BG)</Label>
                  <Input value={pageContent.hero_title_end_bg} onChange={(e) => setPageContent({...pageContent, hero_title_end_bg: e.target.value})} className="mt-1" placeholder="флот" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Description (EN)</Label>
                  <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} className="mt-1" rows={2} />
                </div>
                <div>
                  <Label className="text-xs">Description (BG)</Label>
                  <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} className="mt-1" rows={2} />
                </div>
              </div>

              {/* Features */}
              <h4 className="font-bold text-slate-700 mt-6">Features Checkmarks</h4>
              {pageContent.features_en.map((_, index) => (
                <div key={index} className="grid md:grid-cols-2 gap-4">
                  <Input value={pageContent.features_en[index]} onChange={(e) => updateFeature('en', index, e.target.value)} placeholder={`Feature ${index + 1} (EN)`} />
                  <Input value={pageContent.features_bg[index]} onChange={(e) => updateFeature('bg', index, e.target.value)} placeholder={`Feature ${index + 1} (BG)`} />
                </div>
              ))}

              {/* Grid Section */}
              <h4 className="font-bold text-slate-700 mt-6">Vehicle Grid Section</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Grid Subtitle (EN)</Label>
                  <Input value={pageContent.grid_subtitle_en} onChange={(e) => setPageContent({...pageContent, grid_subtitle_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Grid Subtitle (BG)</Label>
                  <Input value={pageContent.grid_subtitle_bg} onChange={(e) => setPageContent({...pageContent, grid_subtitle_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Grid Title (EN)</Label>
                  <Input value={pageContent.grid_title_en} onChange={(e) => setPageContent({...pageContent, grid_title_en: e.target.value})} className="mt-1" placeholder="Choose the Right" />
                </div>
                <div>
                  <Label className="text-xs">Grid Title (BG)</Label>
                  <Input value={pageContent.grid_title_bg} onChange={(e) => setPageContent({...pageContent, grid_title_bg: e.target.value})} className="mt-1" placeholder="Изберете правилното" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Grid Title Highlight (EN) - colored</Label>
                  <Input value={pageContent.grid_title_highlight_en} onChange={(e) => setPageContent({...pageContent, grid_title_highlight_en: e.target.value})} className="mt-1" placeholder="Vehicle" />
                </div>
                <div>
                  <Label className="text-xs">Grid Title Highlight (BG) - colored</Label>
                  <Input value={pageContent.grid_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, grid_title_highlight_bg: e.target.value})} className="mt-1" placeholder="превозно средство" />
                </div>
              </div>

              {/* CTA Section */}
              <h4 className="font-bold text-slate-700 mt-6">Call to Action</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Title (EN)</Label>
                  <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">CTA Title (BG)</Label>
                  <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Text (EN)</Label>
                  <Textarea value={pageContent.cta_text_en} onChange={(e) => setPageContent({...pageContent, cta_text_en: e.target.value})} className="mt-1" rows={2} />
                </div>
                <div>
                  <Label className="text-xs">CTA Text (BG)</Label>
                  <Textarea value={pageContent.cta_text_bg} onChange={(e) => setPageContent({...pageContent, cta_text_bg: e.target.value})} className="mt-1" rows={2} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Button (EN)</Label>
                  <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">CTA Button (BG)</Label>
                  <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} className="mt-1" />
                </div>
              </div>

              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Fleet Vehicles</h2>
        <Button onClick={() => { setIsFormOpen(true); setEditingVehicle(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Vehicle
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingVehicle ? 'Edit Vehicle' : 'New Vehicle'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingVehicle(null); }}><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Vehicle Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mt-1 rounded-xl" required placeholder="e.g., Mercedes Sprinter" />
                </div>
                <div>
                  <Label>ECO Standard</Label>
                  <Input value={formData.eco_standard} onChange={(e) => setFormData({...formData, eco_standard: e.target.value})} className="mt-1 rounded-xl" placeholder="EURO 6" />
                </div>
              </div>

              {/* Type (EN/BG) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800">🇬🇧 English</h4>
                  <div>
                    <Label>Vehicle Type (EN) *</Label>
                    <Input value={formData.type_en} onChange={(e) => setFormData({...formData, type_en: e.target.value})} className="mt-1 rounded-xl bg-white" required placeholder="e.g., Van, Medium Truck" />
                  </div>
                </div>
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800">🇧🇬 Български</h4>
                  <div>
                    <Label>Тип превозно средство (BG)</Label>
                    <Input value={formData.type_bg} onChange={(e) => setFormData({...formData, type_bg: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="напр., Ван, Среден камион" />
                  </div>
                </div>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Capacity (m³) *</Label>
                  <Input value={formData.capacity_m3} onChange={(e) => setFormData({...formData, capacity_m3: e.target.value})} className="mt-1 rounded-xl" required placeholder="11-16" />
                </div>
                <div>
                  <Label>Payload (kg) *</Label>
                  <Input value={formData.payload_kg} onChange={(e) => setFormData({...formData, payload_kg: e.target.value})} className="mt-1 rounded-xl" required placeholder="1,200" />
                </div>
                <div>
                  <Label>Euro Pallets</Label>
                  <Input value={formData.euro_pallets} onChange={(e) => setFormData({...formData, euro_pallets: e.target.value})} className="mt-1 rounded-xl" placeholder="4-5" />
                </div>
                <div>
                  <Label>Dimensions</Label>
                  <Input value={formData.dimensions} onChange={(e) => setFormData({...formData, dimensions: e.target.value})} className="mt-1 rounded-xl" placeholder="3.5m x 1.8m x 2.0m" />
                </div>
              </div>

              {/* Image and Order */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload 
                      value={formData.image_url} 
                      onChange={(url) => setFormData({...formData, image_url: url})} 
                      placeholder="Enter URL or upload image"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Display Order</Label>
                    <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                      <span className="text-sm text-slate-600">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingVehicle(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save Vehicle</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Vehicles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vehicles.map((vehicle) => (
          <Card key={vehicle.id} className={`bg-white rounded-3xl border-0 shadow-sm ${!vehicle.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-slate-900">{vehicle.name_en || vehicle.name}</h4>
                    {!vehicle.is_active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{vehicle.eco_standard}</span>
                  </div>
                  <p className="text-sm text-[#C41E3A]">{vehicle.type_en || vehicle.type}</p>
                  {vehicle.type_bg && <p className="text-xs text-green-600">BG: {vehicle.type_bg}</p>}
                  {vehicle.name_bg && <p className="text-xs text-green-600">BG: {vehicle.name_bg}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span>📦 {vehicle.capacity_m3 || vehicle.capacity_en} m³</span>
                    <span>⚖️ {vehicle.payload_kg} kg</span>
                    <span>🎯 {vehicle.euro_pallets} pallets</span>
                  </div>
                  {vehicle.dimensions && <p className="text-xs text-slate-400 mt-1">📐 {vehicle.dimensions}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(vehicle)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(vehicle.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {vehicles.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No fleet vehicles in database. The default fleet will be shown. Add your first vehicle to customize!</p>}
      </div>
    </div>
  );
}

// Services Management
function ServicesManager() {
  const [services, setServices] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'OUR SERVICES', hero_subtitle_bg: 'НАШИТЕ УСЛУГИ',
    hero_title_en: 'Comprehensive', hero_title_bg: 'Цялостни',
    hero_title_highlight_en: 'Transport Solutions', hero_title_highlight_bg: 'транспортни решения',
    hero_description_en: 'From express deliveries to full relocations, we offer a complete range of transport services tailored to your needs.',
    hero_description_bg: 'От експресни доставки до пълни премествания, предлагаме пълна гама от транспортни услуги според вашите нужди.',
    grid_subtitle_en: 'WHAT WE OFFER', grid_subtitle_bg: 'КАКВО ПРЕДЛАГАМЕ',
    grid_title_en: 'Our', grid_title_bg: 'Нашите',
    grid_title_highlight_en: 'Services', grid_title_highlight_bg: 'услуги',
    cta_title_en: 'Need a Custom Solution?', cta_title_bg: 'Нуждаете се от персонализирано решение?',
    cta_text_en: 'Contact us to discuss your specific requirements.', cta_text_bg: 'Свържете се с нас за вашите специфични изисквания.',
    cta_button_en: 'Contact Us', cta_button_bg: 'Свържете се с нас'
  });
  
  const iconOptions = ['Truck', 'Package', 'Zap', 'Layers', 'Shield', 'ArrowUpDown', 'Box', 'Globe', 'Clock', 'MapPin'];
  
  const defaultForm = {
    title_en: '', title_bg: '', short_description_en: '', short_description_bg: '',
    full_description_en: '', full_description_bg: '', features_en: '', features_bg: '',
    icon: 'Truck', image_url: '', order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchServices(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/services-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/services-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/services`);
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        features_en: formData.features_en ? formData.features_en.split('\n').filter(f => f.trim()) : [],
        features_bg: formData.features_bg ? formData.features_bg.split('\n').filter(f => f.trim()) : []
      };
      
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, submitData);
        toast.success('Service updated!');
      } else {
        await axios.post(`${API}/admin/services`, submitData);
        toast.success('Service added!');
      }
      fetchServices();
      setIsFormOpen(false);
      setEditingService(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`);
      toast.success('Service deleted!');
      fetchServices();
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const startEdit = (service) => {
    setEditingService(service);
    setFormData({
      title_en: service.title_en || service.title || '',
      title_bg: service.title_bg || '',
      short_description_en: service.short_description_en || service.short_description || '',
      short_description_bg: service.short_description_bg || '',
      full_description_en: service.full_description_en || '',
      full_description_bg: service.full_description_bg || '',
      features_en: Array.isArray(service.features_en) ? service.features_en.join('\n') : '',
      features_bg: Array.isArray(service.features_bg) ? service.features_bg.join('\n') : '',
      icon: service.icon || 'Truck',
      image_url: service.image_url || '',
      order: service.order || 0,
      is_active: service.is_active !== false
    });
    setIsFormOpen(true);
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Services Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
                <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} placeholder="CTA Title EN" />
                <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} placeholder="CTA Title BG" />
                <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} placeholder="CTA Button EN" />
                <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} placeholder="CTA Button BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Services</h2>
          <p className="text-sm text-slate-500">Manage your transport services</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingService(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingService ? 'Edit Service' : 'New Service'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingService(null); }}><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Titles (EN/BG) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800">🇬🇧 English</h4>
                  <div>
                    <Label>Service Title (EN) *</Label>
                    <Input value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} className="mt-1 rounded-xl bg-white" required placeholder="e.g., International Moving" />
                  </div>
                  <div>
                    <Label>Short Description (EN) *</Label>
                    <Textarea value={formData.short_description_en} onChange={(e) => setFormData({...formData, short_description_en: e.target.value})} className="mt-1 rounded-xl bg-white" required rows={2} placeholder="Brief service description..." />
                  </div>
                  <div>
                    <Label>Full Description (EN)</Label>
                    <Textarea value={formData.full_description_en} onChange={(e) => setFormData({...formData, full_description_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="Detailed service description..." />
                  </div>
                  <div>
                    <Label>Features (EN) - one per line</Label>
                    <Textarea value={formData.features_en} onChange={(e) => setFormData({...formData, features_en: e.target.value})} className="mt-1 rounded-xl bg-white" rows={4} placeholder="Feature 1&#10;Feature 2&#10;Feature 3" />
                  </div>
                </div>
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800">🇧🇬 Български</h4>
                  <div>
                    <Label>Заглавие на услугата (BG)</Label>
                    <Input value={formData.title_bg} onChange={(e) => setFormData({...formData, title_bg: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="напр., Международно преместване" />
                  </div>
                  <div>
                    <Label>Кратко описание (BG)</Label>
                    <Textarea value={formData.short_description_bg} onChange={(e) => setFormData({...formData, short_description_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={2} placeholder="Кратко описание на услугата..." />
                  </div>
                  <div>
                    <Label>Пълно описание (BG)</Label>
                    <Textarea value={formData.full_description_bg} onChange={(e) => setFormData({...formData, full_description_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="Подробно описание на услугата..." />
                  </div>
                  <div>
                    <Label>Характеристики (BG) - по една на ред</Label>
                    <Textarea value={formData.features_bg} onChange={(e) => setFormData({...formData, features_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={4} placeholder="Характеристика 1&#10;Характеристика 2&#10;Характеристика 3" />
                  </div>
                </div>
              </div>

              {/* Icon, Image, Order */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select value={formData.icon} onValueChange={(value) => setFormData({...formData, icon: value})}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload 
                      value={formData.image_url} 
                      onChange={(url) => setFormData({...formData, image_url: url})} 
                      placeholder="Enter URL or upload image"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Order</Label>
                    <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingService(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save Service</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Services List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => (
          <Card key={service.id} className={`bg-white rounded-3xl border-0 shadow-sm ${!service.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">
                      {service.icon === 'Truck' && '🚚'}
                      {service.icon === 'Package' && '📦'}
                      {service.icon === 'Zap' && '⚡'}
                      {service.icon === 'Layers' && '📚'}
                      {service.icon === 'Shield' && '🛡️'}
                      {service.icon === 'ArrowUpDown' && '↕️'}
                      {service.icon === 'Box' && '📦'}
                      {service.icon === 'Globe' && '🌍'}
                      {service.icon === 'Clock' && '⏰'}
                      {service.icon === 'MapPin' && '📍'}
                    </span>
                    <h4 className="font-bold text-slate-900">{service.title_en || service.title}</h4>
                    {!service.is_active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  {service.title_bg && <p className="text-xs text-green-600 mb-1">BG: {service.title_bg}</p>}
                  <p className="text-sm text-slate-500 line-clamp-2">{service.short_description_en || service.short_description}</p>
                  {service.features_en && service.features_en.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">{service.features_en.length} features</p>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => startEdit(service)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(service.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {services.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No services in database. The default services will be shown. Add your first service to customize!</p>}
      </div>
    </div>
  );
}

// Destinations Management (Routes & Regions)
function DestinationsManager() {
  const [routes, setRoutes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isRouteFormOpen, setIsRouteFormOpen] = useState(false);
  const [isRegionFormOpen, setIsRegionFormOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('routes');
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'DESTINATIONS',
    hero_subtitle_bg: 'ДЕСТИНАЦИИ',
    hero_title_en: 'Connecting',
    hero_title_bg: 'Свързваме',
    hero_title_highlight_en: 'Europe',
    hero_title_highlight_bg: 'Европа',
    hero_description_en: 'We serve all major European destinations with regular routes and express options.',
    hero_description_bg: 'Обслужваме всички основни европейски дестинации с редовни маршрути и експресни опции.',
    routes_subtitle_en: 'POPULAR ROUTES',
    routes_subtitle_bg: 'ПОПУЛЯРНИ МАРШРУТИ',
    routes_title_en: 'Main',
    routes_title_bg: 'Основни',
    routes_title_highlight_en: 'Transport Lines',
    routes_title_highlight_bg: 'транспортни линии',
    coverage_subtitle_en: 'COVERAGE',
    coverage_subtitle_bg: 'ПОКРИТИЕ',
    coverage_title_en: 'Countries We',
    coverage_title_bg: 'Държави, които',
    coverage_title_highlight_en: 'Serve',
    coverage_title_highlight_bg: 'обслужваме',
    via_en: 'Via',
    via_bg: 'Чрез',
    cta_title_en: 'Destination Not Listed?',
    cta_title_bg: 'Дестинацията не е в списъка?',
    cta_text_en: 'We can arrange transport to virtually any European location.',
    cta_text_bg: 'Можем да организираме транспорт до почти всяка европейска локация.',
    cta_button_en: 'Contact Us',
    cta_button_bg: 'Свържете се с нас'
  });
  
  const [routeFormData, setRouteFormData] = useState({
    name_en: '', name_bg: '', time_en: '', time_bg: '',
    frequency_en: '', frequency_bg: '', via_en: '', via_bg: '',
    order: 0, is_active: true
  });
  
  const [regionFormData, setRegionFormData] = useState({
    name_en: '', name_bg: '', emoji: '🌍',
    countries_en: '', countries_bg: '',
    order: 0, is_active: true
  });

  useEffect(() => {
    fetchData();
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/destinations-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching destinations page content:', error);
    }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/destinations-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) {
      toast.error('Error saving page content');
    } finally {
      setIsSavingContent(false);
    }
  };

  const fetchData = async () => {
    try {
      const [routesRes, regionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/destination-routes`),
        axios.get(`${API_URL}/api/admin/destination-regions`)
      ]);
      setRoutes(routesRes.data.routes || []);
      setRegions(regionsRes.data.regions || []);
    } catch (error) {
      toast.error('Failed to load destinations');
    } finally {
      setIsLoading(false);
    }
  };

  // Routes CRUD
  const handleRouteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoute) {
        await axios.put(`${API_URL}/api/admin/destination-routes/${editingRoute.id}`, routeFormData);
        toast.success('Route updated');
      } else {
        await axios.post(`${API_URL}/api/admin/destination-routes`, routeFormData);
        toast.success('Route added');
      }
      fetchData();
      resetRouteForm();
    } catch (error) {
      toast.error('Failed to save route');
    }
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    setRouteFormData(route);
    setIsRouteFormOpen(true);
  };

  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/destination-routes/${id}`);
      toast.success('Route deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  const resetRouteForm = () => {
    setRouteFormData({
      name_en: '', name_bg: '', time_en: '', time_bg: '',
      frequency_en: '', frequency_bg: '', via_en: '', via_bg: '',
      order: 0, is_active: true
    });
    setEditingRoute(null);
    setIsRouteFormOpen(false);
  };

  // Regions CRUD
  const handleRegionSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...regionFormData,
        countries_en: typeof regionFormData.countries_en === 'string' 
          ? regionFormData.countries_en.split(',').map(c => c.trim()).filter(c => c) 
          : regionFormData.countries_en,
        countries_bg: typeof regionFormData.countries_bg === 'string'
          ? regionFormData.countries_bg.split(',').map(c => c.trim()).filter(c => c)
          : regionFormData.countries_bg
      };
      
      if (editingRegion) {
        await axios.put(`${API_URL}/api/admin/destination-regions/${editingRegion.id}`, dataToSend);
        toast.success('Region updated');
      } else {
        await axios.post(`${API_URL}/api/admin/destination-regions`, dataToSend);
        toast.success('Region added');
      }
      fetchData();
      resetRegionForm();
    } catch (error) {
      toast.error('Failed to save region');
    }
  };

  const handleEditRegion = (region) => {
    setEditingRegion(region);
    setRegionFormData({
      ...region,
      countries_en: Array.isArray(region.countries_en) ? region.countries_en.join(', ') : region.countries_en,
      countries_bg: Array.isArray(region.countries_bg) ? region.countries_bg.join(', ') : region.countries_bg
    });
    setIsRegionFormOpen(true);
  };

  const handleDeleteRegion = async (id) => {
    if (!window.confirm('Delete this region?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/destination-regions/${id}`);
      toast.success('Region deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete region');
    }
  };

  const resetRegionForm = () => {
    setRegionFormData({
      name_en: '', name_bg: '', emoji: '🌍',
      countries_en: '', countries_bg: '',
      order: 0, is_active: true
    });
    setEditingRegion(null);
    setIsRegionFormOpen(false);
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button 
            onClick={() => setShowPageContent(!showPageContent)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Page Content (Titles, Descriptions)</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              {/* Hero Section */}
              <h4 className="font-bold text-slate-700">Hero Section</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Subtitle (EN)</Label>
                  <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Subtitle (BG)</Label>
                  <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title (EN)</Label>
                  <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} className="mt-1" placeholder="Connecting" />
                </div>
                <div>
                  <Label className="text-xs">Title (BG)</Label>
                  <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} className="mt-1" placeholder="Свързваме" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title Highlight (EN) - colored</Label>
                  <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} className="mt-1" placeholder="Europe" />
                </div>
                <div>
                  <Label className="text-xs">Title Highlight (BG) - colored</Label>
                  <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} className="mt-1" placeholder="Европа" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Description (EN)</Label>
                  <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} className="mt-1" rows={2} />
                </div>
                <div>
                  <Label className="text-xs">Description (BG)</Label>
                  <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} className="mt-1" rows={2} />
                </div>
              </div>

              {/* Routes Section */}
              <h4 className="font-bold text-slate-700 mt-6">Routes Section</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Subtitle (EN)</Label>
                  <Input value={pageContent.routes_subtitle_en} onChange={(e) => setPageContent({...pageContent, routes_subtitle_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Subtitle (BG)</Label>
                  <Input value={pageContent.routes_subtitle_bg} onChange={(e) => setPageContent({...pageContent, routes_subtitle_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title (EN)</Label>
                  <Input value={pageContent.routes_title_en} onChange={(e) => setPageContent({...pageContent, routes_title_en: e.target.value})} className="mt-1" placeholder="Main" />
                </div>
                <div>
                  <Label className="text-xs">Title (BG)</Label>
                  <Input value={pageContent.routes_title_bg} onChange={(e) => setPageContent({...pageContent, routes_title_bg: e.target.value})} className="mt-1" placeholder="Основни" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title Highlight (EN)</Label>
                  <Input value={pageContent.routes_title_highlight_en} onChange={(e) => setPageContent({...pageContent, routes_title_highlight_en: e.target.value})} className="mt-1" placeholder="Transport Lines" />
                </div>
                <div>
                  <Label className="text-xs">Title Highlight (BG)</Label>
                  <Input value={pageContent.routes_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, routes_title_highlight_bg: e.target.value})} className="mt-1" placeholder="транспортни линии" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">"Via" label (EN)</Label>
                  <Input value={pageContent.via_en} onChange={(e) => setPageContent({...pageContent, via_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">"Via" label (BG)</Label>
                  <Input value={pageContent.via_bg} onChange={(e) => setPageContent({...pageContent, via_bg: e.target.value})} className="mt-1" />
                </div>
              </div>

              {/* Coverage Section */}
              <h4 className="font-bold text-slate-700 mt-6">Coverage Section</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Subtitle (EN)</Label>
                  <Input value={pageContent.coverage_subtitle_en} onChange={(e) => setPageContent({...pageContent, coverage_subtitle_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Subtitle (BG)</Label>
                  <Input value={pageContent.coverage_subtitle_bg} onChange={(e) => setPageContent({...pageContent, coverage_subtitle_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title (EN)</Label>
                  <Input value={pageContent.coverage_title_en} onChange={(e) => setPageContent({...pageContent, coverage_title_en: e.target.value})} className="mt-1" placeholder="Countries We" />
                </div>
                <div>
                  <Label className="text-xs">Title (BG)</Label>
                  <Input value={pageContent.coverage_title_bg} onChange={(e) => setPageContent({...pageContent, coverage_title_bg: e.target.value})} className="mt-1" placeholder="Държави, които" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Title Highlight (EN)</Label>
                  <Input value={pageContent.coverage_title_highlight_en} onChange={(e) => setPageContent({...pageContent, coverage_title_highlight_en: e.target.value})} className="mt-1" placeholder="Serve" />
                </div>
                <div>
                  <Label className="text-xs">Title Highlight (BG)</Label>
                  <Input value={pageContent.coverage_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, coverage_title_highlight_bg: e.target.value})} className="mt-1" placeholder="обслужваме" />
                </div>
              </div>

              {/* CTA Section */}
              <h4 className="font-bold text-slate-700 mt-6">Call to Action</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Title (EN)</Label>
                  <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">CTA Title (BG)</Label>
                  <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Text (EN)</Label>
                  <Textarea value={pageContent.cta_text_en} onChange={(e) => setPageContent({...pageContent, cta_text_en: e.target.value})} className="mt-1" rows={2} />
                </div>
                <div>
                  <Label className="text-xs">CTA Text (BG)</Label>
                  <Textarea value={pageContent.cta_text_bg} onChange={(e) => setPageContent({...pageContent, cta_text_bg: e.target.value})} className="mt-1" rows={2} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA Button (EN)</Label>
                  <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">CTA Button (BG)</Label>
                  <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} className="mt-1" />
                </div>
              </div>

              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sub-tabs for Routes and Regions */}
      <div className="flex gap-2 border-b pb-4">
        <Button
          variant={activeSubTab === 'routes' ? 'default' : 'outline'}
          onClick={() => setActiveSubTab('routes')}
          className={activeSubTab === 'routes' ? 'bg-[#C41E3A] text-white' : ''}
        >
          🚛 Routes ({routes.length})
        </Button>
        <Button
          variant={activeSubTab === 'regions' ? 'default' : 'outline'}
          onClick={() => setActiveSubTab('regions')}
          className={activeSubTab === 'regions' ? 'bg-[#C41E3A] text-white' : ''}
        >
          🌍 Regions ({regions.length})
        </Button>
      </div>

      {/* ROUTES TAB */}
      {activeSubTab === 'routes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-lg">Transport Routes</h3>
            <Button onClick={() => setIsRouteFormOpen(!isRouteFormOpen)} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Route
            </Button>
          </div>

          {isRouteFormOpen && (
            <Card className="border-[#C41E3A]/20">
              <CardContent className="pt-6">
                <form onSubmit={handleRouteSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Route Name (EN) *</Label>
                      <Input value={routeFormData.name_en} onChange={(e) => setRouteFormData({...routeFormData, name_en: e.target.value})} className="mt-1 rounded-xl" placeholder="Bulgaria - Germany" required />
                    </div>
                    <div>
                      <Label>Route Name (BG)</Label>
                      <Input value={routeFormData.name_bg} onChange={(e) => setRouteFormData({...routeFormData, name_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="България - Германия" />
                    </div>
                    <div>
                      <Label>Time (EN) *</Label>
                      <Input value={routeFormData.time_en} onChange={(e) => setRouteFormData({...routeFormData, time_en: e.target.value})} className="mt-1 rounded-xl" placeholder="24-36 hours" required />
                    </div>
                    <div>
                      <Label>Time (BG)</Label>
                      <Input value={routeFormData.time_bg} onChange={(e) => setRouteFormData({...routeFormData, time_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="24-36 часа" />
                    </div>
                    <div>
                      <Label>Frequency (EN) *</Label>
                      <Input value={routeFormData.frequency_en} onChange={(e) => setRouteFormData({...routeFormData, frequency_en: e.target.value})} className="mt-1 rounded-xl" placeholder="Daily departures" required />
                    </div>
                    <div>
                      <Label>Frequency (BG)</Label>
                      <Input value={routeFormData.frequency_bg} onChange={(e) => setRouteFormData({...routeFormData, frequency_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="Ежедневни заминавания" />
                    </div>
                    <div>
                      <Label>Via (EN)</Label>
                      <Input value={routeFormData.via_en} onChange={(e) => setRouteFormData({...routeFormData, via_en: e.target.value})} className="mt-1 rounded-xl" placeholder="Hungary, Austria" />
                    </div>
                    <div>
                      <Label>Via (BG)</Label>
                      <Input value={routeFormData.via_bg} onChange={(e) => setRouteFormData({...routeFormData, via_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="Унгария, Австрия" />
                    </div>
                    <div>
                      <Label>Order</Label>
                      <Input type="number" value={routeFormData.order} onChange={(e) => setRouteFormData({...routeFormData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={routeFormData.is_active} onChange={(e) => setRouteFormData({...routeFormData, is_active: e.target.checked})} className="rounded" />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                      <Save className="w-4 h-4 mr-2" /> {editingRoute ? 'Update' : 'Add'} Route
                    </Button>
                    <Button type="button" variant="outline" onClick={resetRouteForm} className="rounded-full">
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Routes List */}
          <div className="space-y-2">
            {routes.map((route) => (
              <Card key={route.id} className={`${!route.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold">{route.name_en}</h4>
                      {route.name_bg && <p className="text-sm text-slate-500">{route.name_bg}</p>}
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                        <span>⏱️ {route.time_en}</span>
                        <span>📅 {route.frequency_en}</span>
                        {route.via_en && <span>🛣️ via {route.via_en}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEditRoute(route)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteRoute(route.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {routes.length === 0 && <p className="text-center text-slate-500 py-4">No routes yet. Add your first route!</p>}
          </div>
        </div>
      )}

      {/* REGIONS TAB */}
      {activeSubTab === 'regions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-lg">Coverage Regions</h3>
            <Button onClick={() => setIsRegionFormOpen(!isRegionFormOpen)} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Region
            </Button>
          </div>

          {isRegionFormOpen && (
            <Card className="border-[#C41E3A]/20">
              <CardContent className="pt-6">
                <form onSubmit={handleRegionSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Region Name (EN) *</Label>
                      <Input value={regionFormData.name_en} onChange={(e) => setRegionFormData({...regionFormData, name_en: e.target.value})} className="mt-1 rounded-xl" placeholder="Central Europe" required />
                    </div>
                    <div>
                      <Label>Region Name (BG)</Label>
                      <Input value={regionFormData.name_bg} onChange={(e) => setRegionFormData({...regionFormData, name_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="Централна Европа" />
                    </div>
                    <div>
                      <Label>Emoji</Label>
                      <Input value={regionFormData.emoji} onChange={(e) => setRegionFormData({...regionFormData, emoji: e.target.value})} className="mt-1 rounded-xl" placeholder="🏔️" />
                    </div>
                    <div>
                      <Label>Order</Label>
                      <Input type="number" value={regionFormData.order} onChange={(e) => setRegionFormData({...regionFormData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label>Countries (EN) - comma separated *</Label>
                    <Textarea value={regionFormData.countries_en} onChange={(e) => setRegionFormData({...regionFormData, countries_en: e.target.value})} className="mt-1 rounded-xl" placeholder="Germany, Austria, Switzerland, Czech Republic" rows={2} required />
                  </div>
                  <div>
                    <Label>Countries (BG) - comma separated</Label>
                    <Textarea value={regionFormData.countries_bg} onChange={(e) => setRegionFormData({...regionFormData, countries_bg: e.target.value})} className="mt-1 rounded-xl" placeholder="Германия, Австрия, Швейцария, Чехия" rows={2} />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={regionFormData.is_active} onChange={(e) => setRegionFormData({...regionFormData, is_active: e.target.checked})} className="rounded" />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                      <Save className="w-4 h-4 mr-2" /> {editingRegion ? 'Update' : 'Add'} Region
                    </Button>
                    <Button type="button" variant="outline" onClick={resetRegionForm} className="rounded-full">
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Regions List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {regions.map((region) => (
              <Card key={region.id} className={`${!region.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold">{region.emoji} {region.name_en}</h4>
                      {region.name_bg && <p className="text-sm text-slate-500">{region.name_bg}</p>}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(region.countries_en || []).map((country, i) => (
                          <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded">{country}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEditRegion(region)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteRegion(region.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {regions.length === 0 && <p className="text-center text-slate-500 py-4 col-span-2">No regions yet. Add your first region!</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// Timeline/About Management
function TimelineManager() {
  const [timeline, setTimeline] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'ABOUT TETRALOG', hero_subtitle_bg: 'ЗА TETRALOG',
    hero_title_en: 'Over', hero_title_bg: 'Над',
    hero_title_highlight_en: '20 Years', hero_title_highlight_bg: '20 години',
    hero_title_end_en: 'of Excellence', hero_title_end_bg: 'отлични резултати',
    hero_description_en: 'From humble beginnings to becoming a leading European transport company, our journey is built on trust, reliability, and a commitment to excellence.',
    hero_description_bg: 'От скромни начала до водеща европейска транспортна компания, нашият път е изграден върху доверие, надеждност и ангажимент към съвършенство.',
    mission_subtitle_en: 'OUR MISSION', mission_subtitle_bg: 'НАШАТА МИСИЯ',
    mission_title_en: 'Delivering', mission_title_bg: 'Доставяме',
    mission_title_highlight_en: 'Peace of Mind', mission_title_highlight_bg: 'спокойствие',
    mission_text1_en: 'At Tetralog Ltd., our mission is to provide fast, reliable, and secure international transport services.',
    mission_text1_bg: 'В Tetralog Ltd. нашата мисия е да предоставяме бързи, надеждни и сигурни международни транспортни услуги.',
    mission_text2_en: 'We believe that entrusting your valuables to us should come with complete peace of mind.',
    mission_text2_bg: 'Вярваме, че поверяването на вашите ценности на нас трябва да идва с пълно спокойствие.',
    values: [
      { title_en: 'Reliability', title_bg: 'Надеждност', desc_en: 'We deliver on our promises, every time.', desc_bg: 'Изпълняваме обещанията си винаги.' },
      { title_en: 'Quality Service', title_bg: 'Качествено обслужване', desc_en: 'Exceeding expectations with professional handling.', desc_bg: 'Надхвърляме очакванията с професионално боравене.' },
      { title_en: 'Customer Focus', title_bg: 'Фокус върху клиента', desc_en: 'Your satisfaction is our priority.', desc_bg: 'Вашето удовлетворение е наш приоритет.' },
      { title_en: 'European Reach', title_bg: 'Европейско покритие', desc_en: 'Comprehensive coverage across Europe.', desc_bg: 'Обхващаме всички основни европейски дестинации.' }
    ],
    timeline_subtitle_en: 'OUR JOURNEY', timeline_subtitle_bg: 'НАШИЯТ ПЪТ',
    timeline_title_en: 'Company', timeline_title_bg: 'История на',
    timeline_title_highlight_en: 'History', timeline_title_highlight_bg: 'компанията',
    credentials_subtitle_en: 'CREDENTIALS', credentials_subtitle_bg: 'АКРЕДИТАЦИИ',
    credentials_title_en: 'Licenses &', credentials_title_bg: 'Лицензи и',
    credentials_title_highlight_en: 'Certifications', credentials_title_highlight_bg: 'сертификати',
    credentials_text_en: 'We maintain the highest industry standards and hold all necessary certifications.',
    credentials_text_bg: 'Поддържаме най-високите индустриални стандарти и притежаваме всички необходими сертификати.',
    certifications_en: ['Licensed EU Carrier', 'IRU Member', 'Full Cargo Insurance', 'EURO 6 Standards', 'ECO Zone Certification'],
    certifications_bg: ['Лицензиран EU превозвач', 'Член на IRU', 'Пълна застраховка на товара', 'EURO 6 стандарти', 'ECO зона сертификат'],
    cta_title_en: 'Ready to Work With Us?', cta_title_bg: 'Готови да работите с нас?',
    cta_text_en: 'Join thousands of satisfied clients who trust Tetralog.', cta_text_bg: 'Присъединете се към хилядите доволни клиенти.',
    cta_button_en: 'Get in Touch', cta_button_bg: 'Свържете се с нас'
  });
  
  const defaultForm = {
    year: '',
    title_en: '',
    title_bg: '',
    description_en: '',
    description_bg: '',
    order: 0,
    is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    fetchTimeline();
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/about-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/about-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(`${API}/admin/timeline`);
      const sortedTimeline = response.data.timeline.sort((a, b) => {
        const orderA = parseInt(a.order) || 0;
        const orderB = parseInt(b.order) || 0;
        return orderA - orderB;
      });
      setTimeline(sortedTimeline);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/timeline/${editingItem.id}`, formData);
        toast.success('Timeline item updated!');
      } else {
        await axios.post(`${API}/admin/timeline`, formData);
        toast.success('Timeline item added!');
      }
      fetchTimeline();
      setIsFormOpen(false);
      setEditingItem(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving timeline item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this timeline item?')) return;
    try {
      await axios.delete(`${API}/admin/timeline/${id}`);
      toast.success('Timeline item deleted!');
      fetchTimeline();
    } catch (error) {
      toast.error('Error deleting timeline item');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      year: item.year || '',
      title_en: item.title_en || '',
      title_bg: item.title_bg || '',
      description_en: item.description_en || '',
      description_bg: item.description_bg || '',
      order: item.order || 0,
      is_active: item.is_active !== false
    });
    setIsFormOpen(true);
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">About Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <h4 className="font-bold text-slate-700">Hero Section</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Input value={pageContent.hero_title_end_en} onChange={(e) => setPageContent({...pageContent, hero_title_end_en: e.target.value})} placeholder="Title End EN" />
                <Input value={pageContent.hero_title_end_bg} onChange={(e) => setPageContent({...pageContent, hero_title_end_bg: e.target.value})} placeholder="Title End BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
              </div>
              <h4 className="font-bold text-slate-700 mt-4">Mission Section</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.mission_subtitle_en} onChange={(e) => setPageContent({...pageContent, mission_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.mission_subtitle_bg} onChange={(e) => setPageContent({...pageContent, mission_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.mission_title_en} onChange={(e) => setPageContent({...pageContent, mission_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.mission_title_bg} onChange={(e) => setPageContent({...pageContent, mission_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.mission_title_highlight_en} onChange={(e) => setPageContent({...pageContent, mission_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.mission_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, mission_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.mission_text1_en} onChange={(e) => setPageContent({...pageContent, mission_text1_en: e.target.value})} placeholder="Text 1 EN" rows={2} />
                <Textarea value={pageContent.mission_text1_bg} onChange={(e) => setPageContent({...pageContent, mission_text1_bg: e.target.value})} placeholder="Text 1 BG" rows={2} />
              </div>
              <h4 className="font-bold text-slate-700 mt-4">CTA Section</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.cta_title_en} onChange={(e) => setPageContent({...pageContent, cta_title_en: e.target.value})} placeholder="CTA Title EN" />
                <Input value={pageContent.cta_title_bg} onChange={(e) => setPageContent({...pageContent, cta_title_bg: e.target.value})} placeholder="CTA Title BG" />
                <Input value={pageContent.cta_button_en} onChange={(e) => setPageContent({...pageContent, cta_button_en: e.target.value})} placeholder="Button EN" />
                <Input value={pageContent.cta_button_bg} onChange={(e) => setPageContent({...pageContent, cta_button_bg: e.target.value})} placeholder="Button BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Company Timeline</h2>
          <p className="text-sm text-slate-500">Manage timeline on About page</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingItem(null); setFormData({...defaultForm, order: timeline.length + 1}); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Year
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Timeline Item' : 'New Timeline Item'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingItem(null); }}><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Year and Order */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Year *</Label>
                  <Input value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} className="mt-1 rounded-xl" required placeholder="e.g., 2013 or Today" />
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="mt-1 rounded-xl" />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                    <span className="text-sm text-slate-600">Active (visible on site)</span>
                  </label>
                </div>
              </div>

              {/* Title and Description (EN/BG) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-bold text-blue-800">🇬🇧 English</h4>
                  <div>
                    <Label>Title (EN) *</Label>
                    <Input value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} className="mt-1 rounded-xl bg-white" required placeholder="e.g., Company Founded" />
                  </div>
                  <div>
                    <Label>Description (EN) *</Label>
                    <Textarea value={formData.description_en} onChange={(e) => setFormData({...formData, description_en: e.target.value})} className="mt-1 rounded-xl bg-white" required rows={3} placeholder="Describe what happened this year..." />
                  </div>
                </div>
                <div className="space-y-4 p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-bold text-green-800">🇧🇬 Български</h4>
                  <div>
                    <Label>Заглавие (BG)</Label>
                    <Input value={formData.title_bg} onChange={(e) => setFormData({...formData, title_bg: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="напр., Основаване на компанията" />
                  </div>
                  <div>
                    <Label>Описание (BG)</Label>
                    <Textarea value={formData.description_bg} onChange={(e) => setFormData({...formData, description_bg: e.target.value})} className="mt-1 rounded-xl bg-white" rows={3} placeholder="Опишете какво се е случило през тази година..." />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingItem(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Timeline List */}
      <div className="space-y-3">
        {timeline.map((item) => (
          <Card key={item.id} className={`bg-white rounded-2xl border-0 shadow-sm ${!item.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
                    <span className="text-[#C41E3A] font-bold text-lg">{item.year}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-slate-900">{item.title_en}</h4>
                      {!item.is_active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
                    </div>
                    {item.title_bg && <p className="text-xs text-green-600">BG: {item.title_bg}</p>}
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description_en}</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => startEdit(item)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {timeline.length === 0 && <p className="text-slate-500 text-center py-8">No timeline items yet. Add your company&apos;s history!</p>}
      </div>
    </div>
  );
}

// FAQ Manager Component
function FAQManager() {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'FAQ', hero_subtitle_bg: 'ЧЗВ',
    hero_title_en: 'Frequently Asked', hero_title_bg: 'Често задавани',
    hero_title_highlight_en: 'Questions', hero_title_highlight_bg: 'въпроси',
    hero_description_en: 'Find answers to common questions about our services, pricing, and processes.',
    hero_description_bg: 'Намерете отговори на често задавани въпроси за нашите услуги, цени и процеси.',
    still_have_questions_en: 'Still Have Questions?', still_have_questions_bg: 'Все още имате въпроси?',
    support_text_en: 'Our customer support team is here to help you with any questions.',
    support_text_bg: 'Нашият екип за клиентска поддръжка е тук, за да ви помогне с всякакви въпроси.',
    contact_support_en: 'Contact Support', contact_support_bg: 'Свържете се с поддръжка',
    ready_to_start_en: 'Ready to Get Started?', ready_to_start_bg: 'Готови да започнете?',
    quote_text_en: 'Request a personalized quote for your transport needs.',
    quote_text_bg: 'Заявете персонализирана оферта за вашите транспортни нужди.',
    request_quote_en: 'Request Quote', request_quote_bg: 'Заявете оферта'
  });
  const [formData, setFormData] = useState({
    question_en: '', question_bg: '', answer_en: '', answer_bg: '', order: 0, is_active: true
  });

  useEffect(() => { fetchFAQs(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/faq-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/faq-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faq`);
      // Sort by order
      const sortedFaqs = response.data.faqs.sort((a, b) => (parseInt(a.order) || 0) - (parseInt(b.order) || 0));
      setFaqs(sortedFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, order: parseInt(formData.order) || 0 };
      if (editingId) {
        await axios.put(`${API}/admin/faq/${editingId}`, submitData);
        toast.success('FAQ updated!');
      } else {
        await axios.post(`${API}/admin/faq`, submitData);
        toast.success('FAQ created!');
      }
      resetForm();
      fetchFAQs();
    } catch (error) {
      toast.error('Error saving FAQ');
    }
  };

  const startEdit = (faq) => {
    setEditingId(faq.id);
    setFormData({
      question_en: faq.question_en || '',
      question_bg: faq.question_bg || '',
      answer_en: faq.answer_en || '',
      answer_bg: faq.answer_bg || '',
      order: faq.order || 0,
      is_active: faq.is_active !== false
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`);
      toast.success('FAQ deleted!');
      fetchFAQs();
    } catch (error) {
      toast.error('Error deleting FAQ');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ question_en: '', question_bg: '', answer_en: '', answer_bg: '', order: 0, is_active: true });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">FAQ Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {showPageContent && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Input value={pageContent.hero_subtitle_en} onChange={(e) => setPageContent({...pageContent, hero_subtitle_en: e.target.value})} placeholder="Subtitle EN" />
                <Input value={pageContent.hero_subtitle_bg} onChange={(e) => setPageContent({...pageContent, hero_subtitle_bg: e.target.value})} placeholder="Subtitle BG" />
                <Input value={pageContent.hero_title_en} onChange={(e) => setPageContent({...pageContent, hero_title_en: e.target.value})} placeholder="Title EN" />
                <Input value={pageContent.hero_title_bg} onChange={(e) => setPageContent({...pageContent, hero_title_bg: e.target.value})} placeholder="Title BG" />
                <Input value={pageContent.hero_title_highlight_en} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_en: e.target.value})} placeholder="Highlight EN" />
                <Input value={pageContent.hero_title_highlight_bg} onChange={(e) => setPageContent({...pageContent, hero_title_highlight_bg: e.target.value})} placeholder="Highlight BG" />
                <Textarea value={pageContent.hero_description_en} onChange={(e) => setPageContent({...pageContent, hero_description_en: e.target.value})} placeholder="Description EN" rows={2} />
                <Textarea value={pageContent.hero_description_bg} onChange={(e) => setPageContent({...pageContent, hero_description_bg: e.target.value})} placeholder="Description BG" rows={2} />
                <Input value={pageContent.still_have_questions_en} onChange={(e) => setPageContent({...pageContent, still_have_questions_en: e.target.value})} placeholder="Still Have Questions EN" />
                <Input value={pageContent.still_have_questions_bg} onChange={(e) => setPageContent({...pageContent, still_have_questions_bg: e.target.value})} placeholder="Still Have Questions BG" />
                <Input value={pageContent.contact_support_en} onChange={(e) => setPageContent({...pageContent, contact_support_en: e.target.value})} placeholder="Contact Support EN" />
                <Input value={pageContent.contact_support_bg} onChange={(e) => setPageContent({...pageContent, contact_support_bg: e.target.value})} placeholder="Contact Support BG" />
              </div>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-900">FAQ Management</h3>
          <p className="text-sm text-slate-500">Add, edit, or remove frequently asked questions</p>
        </div>
        <Button onClick={resetForm} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add New FAQ
        </Button>
      </div>

      {/* Form */}
      <Card className="bg-white rounded-3xl shadow-lg">
        <CardContent className="p-6">
          <h4 className="font-semibold text-slate-900 mb-4">{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Label>Question (English) *</Label>
                <Input
                  required
                  value={formData.question_en}
                  onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="Enter question in English"
                />
              </div>
              <div>
                <Label>Question (Bulgarian)</Label>
                <Input
                  value={formData.question_bg}
                  onChange={(e) => setFormData({ ...formData, question_bg: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="Въведете въпрос на български"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Label>Answer (English) *</Label>
                <Textarea
                  required
                  value={formData.answer_en}
                  onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
                  className="mt-1 rounded-xl"
                  rows={3}
                  placeholder="Enter answer in English"
                />
              </div>
              <div>
                <Label>Answer (Bulgarian)</Label>
                <Textarea
                  value={formData.answer_bg}
                  onChange={(e) => setFormData({ ...formData, answer_bg: e.target.value })}
                  className="mt-1 rounded-xl"
                  rows={3}
                  placeholder="Въведете отговор на български"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label>Active (visible on site)</Label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Create'} FAQ
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-full">
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* FAQ List */}
      <div className="space-y-3">
        {faqs.map((faq) => (
          <Card key={faq.id} className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow ${!faq.is_active ? 'opacity-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">#{faq.order}</span>
                    {!faq.is_active && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">{faq.question_en}</h4>
                  {faq.question_bg && <p className="text-sm text-slate-500 mb-2">🇧🇬 {faq.question_bg}</p>}
                  <p className="text-sm text-slate-600">{faq.answer_en?.substring(0, 150)}...</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => startEdit(faq)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(faq.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {faqs.length === 0 && <p className="text-slate-500 text-center py-8">No FAQs yet. Add your first question!</p>}
      </div>
    </div>
  );
}

// Legal Pages Manager Component
function LegalPagesManager() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_bg: '', content_en: '', content_bg: ''
  });

  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/legal`);
      setPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingId) return;
    try {
      await axios.put(`${API}/admin/legal/${editingId}`, formData);
      toast.success('Page saved successfully!');
      fetchPages();
      setEditingId(null);
    } catch (error) {
      toast.error('Error saving page');
    }
  };

  const startEdit = (page) => {
    setEditingId(page.id);
    setFormData({
      title_en: page.title_en || '',
      title_bg: page.title_bg || '',
      content_en: page.content_en || '',
      content_bg: page.content_bg || ''
    });
  };

  const getPageLabel = (id) => {
    const labels = {
      'privacy-policy': { en: 'Privacy Policy', bg: 'Политика за поверителност', icon: '🔒' },
      'terms-of-service': { en: 'Terms of Service', bg: 'Условия за ползване', icon: '📋' },
      'cookie-policy': { en: 'Cookie Policy', bg: 'Политика за бисквитки', icon: '🍪' }
    };
    return labels[id] || { en: id, bg: id, icon: '📄' };
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-slate-900">Legal Pages</h3>
        <p className="text-sm text-slate-500">Manage Privacy Policy, Terms of Service, and Cookie Policy</p>
      </div>

      {/* Page Selection */}
      {!editingId && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {pages.map((page) => {
            const label = getPageLabel(page.id);
            const hasContent = page.content_en && page.content_en.length > 50;
            return (
              <Card 
                key={page.id} 
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => startEdit(page)}
              >
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">{label.icon}</div>
                  <h4 className="font-semibold text-slate-900 mb-1">{label.en}</h4>
                  <p className="text-sm text-slate-500 mb-3">{label.bg}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${hasContent ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {hasContent ? 'Content Added' : 'Needs Content'}
                    </span>
                    <Button variant="outline" size="sm" className="rounded-full">
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Form */}
      {editingId && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getPageLabel(editingId).icon}</span>
                <div>
                  <h4 className="font-semibold text-slate-900">{getPageLabel(editingId).en}</h4>
                  <p className="text-sm text-slate-500">{getPageLabel(editingId).bg}</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setEditingId(null)} className="rounded-full">
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>

            <div className="space-y-6">
              {/* Titles */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label>Page Title (English)</Label>
                  <Input
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    className="mt-1 rounded-xl"
                    placeholder="Privacy Policy"
                  />
                </div>
                <div>
                  <Label>Page Title (Bulgarian)</Label>
                  <Input
                    value={formData.title_bg}
                    onChange={(e) => setFormData({ ...formData, title_bg: e.target.value })}
                    className="mt-1 rounded-xl"
                    placeholder="Политика за поверителност"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label>Content (English)</Label>
                  <Textarea
                    value={formData.content_en}
                    onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                    className="mt-1 rounded-xl font-mono text-sm"
                    rows={15}
                    placeholder="Enter the full content in English. You can use line breaks for paragraphs."
                  />
                  <p className="text-xs text-slate-400 mt-1">Tip: Use blank lines to separate paragraphs</p>
                </div>
                <div>
                  <Label>Content (Bulgarian)</Label>
                  <Textarea
                    value={formData.content_bg}
                    onChange={(e) => setFormData({ ...formData, content_bg: e.target.value })}
                    className="mt-1 rounded-xl font-mono text-sm"
                    rows={15}
                    placeholder="Въведете пълното съдържание на български. Можете да използвате нови редове за параграфи."
                  />
                  <p className="text-xs text-slate-400 mt-1">Съвет: Използвайте празни редове за разделяне на параграфи</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSave} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                  <Save className="w-4 h-4 mr-2" /> Save Page
                </Button>
                <a href={`/legal/${editingId}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full">
                    <Eye className="w-4 h-4 mr-2" /> Preview Page
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Home Page Manager Component
function HomeManager() {
  const [homeContent, setHomeContent] = useState({
    hero_title_en: '',
    hero_title_bg: '',
    hero_subtitle_en: '',
    hero_subtitle_bg: '',
    hero_image: '/images/truck-hero.jpg',
    stats: [
      { number: '', label_en: '', label_bg: '' },
      { number: '', label_en: '', label_bg: '' },
      { number: '', label_en: '', label_bg: '' },
      { number: '', label_en: '', label_bg: '' }
    ],
    why_choose_title_en: '',
    why_choose_title_bg: '',
    why_choose_subtitle_en: '',
    why_choose_subtitle_bg: '',
    why_choose_features_en: ['', '', '', '', ''],
    why_choose_features_bg: ['', '', '', '', '']
  });
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', company: '', text_en: '', text_bg: '', rating: 5 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchHomeContent();
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`);
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleSaveTestimonial = async () => {
    try {
      if (editingTestimonialId) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonialId}`, testimonialForm);
        toast.success('Review updated!');
      } else {
        await axios.post(`${API}/admin/testimonials`, testimonialForm);
        toast.success('Review added!');
      }
      fetchTestimonials();
      setShowTestimonialForm(false);
      setEditingTestimonialId(null);
      setTestimonialForm({ name: '', company: '', text_en: '', text_bg: '', rating: 5 });
    } catch (error) {
      toast.error('Failed to save review');
    }
  };

  const handleEditTestimonial = (t) => {
    setTestimonialForm({ name: t.name, company: t.company, text_en: t.text_en, text_bg: t.text_bg, rating: t.rating });
    setEditingTestimonialId(t.id);
    setShowTestimonialForm(true);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success('Review deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const fetchHomeContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/home-content`);
      if (response.data) {
        setHomeContent({
          ...homeContent,
          ...response.data,
          stats: response.data.stats || homeContent.stats,
          why_choose_features_en: response.data.why_choose_features_en || homeContent.why_choose_features_en,
          why_choose_features_bg: response.data.why_choose_features_bg || homeContent.why_choose_features_bg
        });
      }
    } catch (error) {
      console.error('Error fetching home content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/home-content`, homeContent);
      toast.success('Home page content saved!');
    } catch (error) {
      toast.error('Failed to save home content');
    } finally {
      setIsSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...homeContent.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setHomeContent({ ...homeContent, stats: newStats });
  };

  const updateFeature = (lang, index, value) => {
    const field = `why_choose_features_${lang}`;
    const newFeatures = [...homeContent[field]];
    newFeatures[index] = value;
    setHomeContent({ ...homeContent, [field]: newFeatures });
  };

  const addFeature = () => {
    setHomeContent({
      ...homeContent,
      why_choose_features_en: [...homeContent.why_choose_features_en, ''],
      why_choose_features_bg: [...homeContent.why_choose_features_bg, '']
    });
  };

  const removeFeature = (index) => {
    setHomeContent({
      ...homeContent,
      why_choose_features_en: homeContent.why_choose_features_en.filter((_, i) => i !== index),
      why_choose_features_bg: homeContent.why_choose_features_bg.filter((_, i) => i !== index)
    });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-[#C41E3A]" /> Hero Section
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title (EN)</Label>
              <Input
                value={homeContent.hero_title_en}
                onChange={(e) => setHomeContent({ ...homeContent, hero_title_en: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Title (BG)</Label>
              <Input
                value={homeContent.hero_title_bg}
                onChange={(e) => setHomeContent({ ...homeContent, hero_title_bg: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Subtitle (EN)</Label>
              <Textarea
                value={homeContent.hero_subtitle_en}
                onChange={(e) => setHomeContent({ ...homeContent, hero_subtitle_en: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Subtitle (BG)</Label>
              <Textarea
                value={homeContent.hero_subtitle_bg}
                onChange={(e) => setHomeContent({ ...homeContent, hero_subtitle_bg: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <div>
            <Label>Hero Image</Label>
            <ImageUpload
              currentImage={homeContent.hero_image}
              onImageChange={(url) => setHomeContent({ ...homeContent, hero_image: url })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C41E3A]" /> Statistics
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            {homeContent.stats.map((stat, index) => (
              <div key={index} className="p-4 border rounded-xl bg-slate-50">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Number</Label>
                    <Input
                      value={stat.number}
                      onChange={(e) => updateStat(index, 'number', e.target.value)}
                      placeholder="20+"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Label (EN)</Label>
                    <Input
                      value={stat.label_en}
                      onChange={(e) => updateStat(index, 'label_en', e.target.value)}
                      placeholder="Years Experience"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Label (BG)</Label>
                    <Input
                      value={stat.label_bg}
                      onChange={(e) => updateStat(index, 'label_bg', e.target.value)}
                      placeholder="Години опит"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Why Choose Us Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C41E3A]" /> Why Choose Us Section
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title (EN)</Label>
              <Input
                value={homeContent.why_choose_title_en}
                onChange={(e) => setHomeContent({ ...homeContent, why_choose_title_en: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Title (BG)</Label>
              <Input
                value={homeContent.why_choose_title_bg}
                onChange={(e) => setHomeContent({ ...homeContent, why_choose_title_bg: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Subtitle (EN)</Label>
              <Textarea
                value={homeContent.why_choose_subtitle_en}
                onChange={(e) => setHomeContent({ ...homeContent, why_choose_subtitle_en: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Subtitle (BG)</Label>
              <Textarea
                value={homeContent.why_choose_subtitle_bg}
                onChange={(e) => setHomeContent({ ...homeContent, why_choose_subtitle_bg: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <h4 className="font-medium text-slate-700 mb-3">Features / Checkmarks</h4>
          {homeContent.why_choose_features_en.map((_, index) => (
            <div key={index} className="grid md:grid-cols-2 gap-4 mb-2 items-start">
              <div className="flex gap-2">
                <Input
                  value={homeContent.why_choose_features_en[index]}
                  onChange={(e) => updateFeature('en', index, e.target.value)}
                  placeholder={`Feature ${index + 1} (EN)`}
                />
              </div>
              <div className="flex gap-2">
                <Input
                  value={homeContent.why_choose_features_bg[index]}
                  onChange={(e) => updateFeature('bg', index, e.target.value)}
                  placeholder={`Feature ${index + 1} (BG)`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addFeature}
            className="mt-2"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Feature
          </Button>
        </CardContent>
      </Card>

      {/* Testimonials Section - integrated into Home */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#C41E3A]" /> Customer Reviews
            </h3>
            <Button onClick={() => setShowTestimonialForm(!showTestimonialForm)} size="sm" className="bg-[#C41E3A] hover:bg-[#A01830]">
              <Plus className="w-4 h-4 mr-1" /> Add Review
            </Button>
          </div>

          {showTestimonialForm && (
            <div className="mb-6 p-4 border rounded-xl bg-slate-50">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={testimonialForm.name}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={testimonialForm.company}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })}
                    placeholder="Company Name"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Review Text (EN)</Label>
                  <Textarea
                    value={testimonialForm.text_en}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, text_en: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Review Text (BG)</Label>
                  <Textarea
                    value={testimonialForm.text_bg}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, text_bg: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
              <div className="mb-4">
                <Label>Rating</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })}
                      className="p-1"
                    >
                      <Star className={`w-6 h-6 ${star <= testimonialForm.rating ? 'fill-[#C41E3A] text-[#C41E3A]' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveTestimonial} className="bg-[#C41E3A] hover:bg-[#A01830]">
                  <Save className="w-4 h-4 mr-1" /> {editingTestimonialId ? 'Update' : 'Add'}
                </Button>
                <Button variant="outline" onClick={() => { setShowTestimonialForm(false); setEditingTestimonialId(null); setTestimonialForm({ name: '', company: '', text_en: '', text_bg: '', rating: 5 }); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {testimonials.map((t) => (
              <div key={t.id} className="p-4 border rounded-xl bg-slate-50 flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex gap-1 mb-1">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#C41E3A] text-[#C41E3A]" />
                    ))}
                  </div>
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.company}</div>
                  <div className="mt-1 text-sm text-slate-600 italic">EN: "{t.text_en}"</div>
                  <div className="text-sm text-slate-600 italic">BG: "{t.text_bg}"</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEditTestimonial(t)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTestimonial(t.id)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {testimonials.length === 0 && (
              <p className="text-center text-slate-500 py-4">No reviews yet.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
        <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Home Page Content'}
      </Button>
    </div>
  );
}

// Why Choose Us Page Manager Component
function WhyChooseUsManager() {
  const [content, setContent] = useState({
    hero_subtitle_en: 'WHY CHOOSE US',
    hero_subtitle_bg: 'ЗАЩО ДА ИЗБЕРЕТЕ НАС',
    hero_title_en: 'Your Trusted',
    hero_title_bg: 'Вашият доверен',
    hero_title_highlight_en: 'Transport Partner',
    hero_title_highlight_bg: 'транспортен партньор',
    hero_description_en: '',
    hero_description_bg: '',
    stats: [
      { number: '20+', label_en: 'Years Experience', label_bg: 'Години опит' },
      { number: '50,000+', label_en: 'Shipments Delivered', label_bg: 'Доставени пратки' },
      { number: '99.5%', label_en: 'On-Time Rate', label_bg: 'Навременност' },
      { number: '28+', label_en: 'Countries Served', label_bg: 'Обслужени държави' }
    ],
    benefits_subtitle_en: 'OUR ADVANTAGES',
    benefits_subtitle_bg: 'НАШИТЕ ПРЕДИМСТВА',
    benefits_title_en: 'What Sets Us',
    benefits_title_bg: 'Какво ни прави',
    benefits_title_highlight_en: 'Apart',
    benefits_title_highlight_bg: 'различни',
    benefits: [
      { title_en: 'Fully Insured', title_bg: 'Пълна застраховка', description_en: 'All shipments are covered by comprehensive cargo insurance.', description_bg: 'Всички пратки са покрити с цялостна застраховка на товара.', highlight_en: '100% Coverage', highlight_bg: '100% покритие' },
      { title_en: 'Modern Fleet', title_bg: 'Модерен флот', description_en: 'EURO 6 compliant vehicles with ECO zone access.', description_bg: 'EURO 6 превозни средства с достъп до ECO зони.', highlight_en: 'EURO 6 Standard', highlight_bg: 'EURO 6 стандарт' },
      { title_en: 'GPS Tracking', title_bg: 'GPS проследяване', description_en: 'Real-time tracking on every shipment.', description_bg: 'Проследяване в реално време на всяка пратка.', highlight_en: 'Live Updates', highlight_bg: 'На живо' },
      { title_en: 'Express Delivery', title_bg: 'Експресна доставка', description_en: 'Time-critical shipments delivered within 24-40 hours.', description_bg: 'Времево критични пратки доставени в рамките на 24-40 часа.', highlight_en: '24-40 Hours', highlight_bg: '24-40 часа' },
      { title_en: '24/7 Support', title_bg: '24/7 поддръжка', description_en: 'Our dedicated team is available around the clock.', description_bg: 'Нашият екип е на разположение денонощно.', highlight_en: 'Always Available', highlight_bg: 'Винаги на линия' },
      { title_en: 'Licensed Carrier', title_bg: 'Лицензиран превозвач', description_en: 'Fully licensed EU carrier and IRU member.', description_bg: 'Напълно лицензиран EU превозвач и член на IRU.', highlight_en: 'IRU Member', highlight_bg: 'IRU член' }
    ],
    guarantees_subtitle_en: 'QUALITY ASSURANCE',
    guarantees_subtitle_bg: 'ГАРАНЦИЯ ЗА КАЧЕСТВО',
    guarantees_title_en: 'Our',
    guarantees_title_bg: 'Нашите',
    guarantees_title_highlight_en: 'Guarantees',
    guarantees_title_highlight_bg: 'гаранции',
    guarantees_text_en: '',
    guarantees_text_bg: '',
    guarantees_en: ['On-time delivery or money back guarantee', 'Professional handling of all cargo types', 'Transparent pricing with no hidden fees', 'Regular updates throughout transport', 'Flexible scheduling options', 'Customs clearance assistance'],
    guarantees_bg: ['Навременна доставка или връщане на парите', 'Професионално боравене с всички видове товари', 'Прозрачно ценообразуване без скрити такси', 'Редовни актуализации по време на транспорта', 'Гъвкави опции за планиране', 'Помощ при митническо оформяне'],
    cta_title_en: 'Ready to Experience the Difference?',
    cta_title_bg: 'Готови да изпитате разликата?',
    cta_text_en: '',
    cta_text_bg: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/why-choose-us-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setContent({ ...content, ...response.data });
      }
    } catch (error) {
      console.error('Error fetching why choose us content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/why-choose-us-content`, content);
      toast.success('Why Choose Us page saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent({ ...content, stats: newStats });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...content.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    setContent({ ...content, benefits: newBenefits });
  };

  const updateGuarantee = (lang, index, value) => {
    const field = `guarantees_${lang}`;
    const newGuarantees = [...content[field]];
    newGuarantees[index] = value;
    setContent({ ...content, [field]: newGuarantees });
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C41E3A]" /> Hero Section
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Subtitle (EN)</Label>
              <Input value={content.hero_subtitle_en} onChange={(e) => setContent({ ...content, hero_subtitle_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Subtitle (BG)</Label>
              <Input value={content.hero_subtitle_bg} onChange={(e) => setContent({ ...content, hero_subtitle_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title (EN)</Label>
              <Input value={content.hero_title_en} onChange={(e) => setContent({ ...content, hero_title_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Title (BG)</Label>
              <Input value={content.hero_title_bg} onChange={(e) => setContent({ ...content, hero_title_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title Highlight (EN) - colored part</Label>
              <Input value={content.hero_title_highlight_en} onChange={(e) => setContent({ ...content, hero_title_highlight_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Title Highlight (BG) - colored part</Label>
              <Input value={content.hero_title_highlight_bg} onChange={(e) => setContent({ ...content, hero_title_highlight_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Description (EN)</Label>
              <Textarea value={content.hero_description_en} onChange={(e) => setContent({ ...content, hero_description_en: e.target.value })} className="mt-1" rows={2} />
            </div>
            <div>
              <Label>Description (BG)</Label>
              <Textarea value={content.hero_description_bg} onChange={(e) => setContent({ ...content, hero_description_bg: e.target.value })} className="mt-1" rows={2} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C41E3A]" /> Statistics
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {content.stats.map((stat, index) => (
              <div key={index} className="p-3 border rounded-xl bg-slate-50">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Number</Label>
                    <Input value={stat.number} onChange={(e) => updateStat(index, 'number', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Label (EN)</Label>
                    <Input value={stat.label_en} onChange={(e) => updateStat(index, 'label_en', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Label (BG)</Label>
                    <Input value={stat.label_bg} onChange={(e) => updateStat(index, 'label_bg', e.target.value)} className="mt-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#C41E3A]" /> Benefits / Advantages
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Section Subtitle (EN) - small text above title</Label>
              <Input value={content.benefits_subtitle_en} onChange={(e) => setContent({ ...content, benefits_subtitle_en: e.target.value })} className="mt-1" placeholder="OUR ADVANTAGES" />
            </div>
            <div>
              <Label>Section Subtitle (BG)</Label>
              <Input value={content.benefits_subtitle_bg} onChange={(e) => setContent({ ...content, benefits_subtitle_bg: e.target.value })} className="mt-1" placeholder="НАШИТЕ ПРЕДИМСТВА" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Section Title (EN)</Label>
              <Input value={content.benefits_title_en} onChange={(e) => setContent({ ...content, benefits_title_en: e.target.value })} className="mt-1" placeholder="What Sets Us" />
            </div>
            <div>
              <Label>Section Title (BG)</Label>
              <Input value={content.benefits_title_bg} onChange={(e) => setContent({ ...content, benefits_title_bg: e.target.value })} className="mt-1" placeholder="Какво ни прави" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title Highlight (EN) - colored part</Label>
              <Input value={content.benefits_title_highlight_en} onChange={(e) => setContent({ ...content, benefits_title_highlight_en: e.target.value })} className="mt-1" placeholder="Apart" />
            </div>
            <div>
              <Label>Title Highlight (BG) - colored part</Label>
              <Input value={content.benefits_title_highlight_bg} onChange={(e) => setContent({ ...content, benefits_title_highlight_bg: e.target.value })} className="mt-1" placeholder="различни" />
            </div>
          </div>
          <div className="space-y-4">
            {content.benefits.map((benefit, index) => (
              <div key={index} className="p-4 border rounded-xl bg-slate-50">
                <div className="grid md:grid-cols-2 gap-3 mb-2">
                  <Input value={benefit.title_en} onChange={(e) => updateBenefit(index, 'title_en', e.target.value)} placeholder="Title (EN)" />
                  <Input value={benefit.title_bg} onChange={(e) => updateBenefit(index, 'title_bg', e.target.value)} placeholder="Title (BG)" />
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-2">
                  <Textarea value={benefit.description_en} onChange={(e) => updateBenefit(index, 'description_en', e.target.value)} placeholder="Description (EN)" rows={2} />
                  <Textarea value={benefit.description_bg} onChange={(e) => updateBenefit(index, 'description_bg', e.target.value)} placeholder="Description (BG)" rows={2} />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Input value={benefit.highlight_en} onChange={(e) => updateBenefit(index, 'highlight_en', e.target.value)} placeholder="Highlight badge (EN)" />
                  <Input value={benefit.highlight_bg} onChange={(e) => updateBenefit(index, 'highlight_bg', e.target.value)} placeholder="Highlight badge (BG)" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Guarantees */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#C41E3A]" /> Guarantees
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Section Subtitle (EN) - small text above title</Label>
              <Input value={content.guarantees_subtitle_en} onChange={(e) => setContent({ ...content, guarantees_subtitle_en: e.target.value })} className="mt-1" placeholder="QUALITY ASSURANCE" />
            </div>
            <div>
              <Label>Section Subtitle (BG)</Label>
              <Input value={content.guarantees_subtitle_bg} onChange={(e) => setContent({ ...content, guarantees_subtitle_bg: e.target.value })} className="mt-1" placeholder="ГАРАНЦИЯ ЗА КАЧЕСТВО" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Section Title (EN)</Label>
              <Input value={content.guarantees_title_en} onChange={(e) => setContent({ ...content, guarantees_title_en: e.target.value })} className="mt-1" placeholder="Our" />
            </div>
            <div>
              <Label>Section Title (BG)</Label>
              <Input value={content.guarantees_title_bg} onChange={(e) => setContent({ ...content, guarantees_title_bg: e.target.value })} className="mt-1" placeholder="Нашите" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Title Highlight (EN) - colored part</Label>
              <Input value={content.guarantees_title_highlight_en} onChange={(e) => setContent({ ...content, guarantees_title_highlight_en: e.target.value })} className="mt-1" placeholder="Guarantees" />
            </div>
            <div>
              <Label>Title Highlight (BG) - colored part</Label>
              <Input value={content.guarantees_title_highlight_bg} onChange={(e) => setContent({ ...content, guarantees_title_highlight_bg: e.target.value })} className="mt-1" placeholder="гаранции" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Description (EN)</Label>
              <Textarea value={content.guarantees_text_en} onChange={(e) => setContent({ ...content, guarantees_text_en: e.target.value })} className="mt-1" rows={2} />
            </div>
            <div>
              <Label>Description (BG)</Label>
              <Textarea value={content.guarantees_text_bg} onChange={(e) => setContent({ ...content, guarantees_text_bg: e.target.value })} className="mt-1" rows={2} />
            </div>
          </div>
          <h4 className="font-medium text-slate-700 mb-3">Guarantee Checkmarks</h4>
          {content.guarantees_en.map((_, index) => (
            <div key={index} className="grid md:grid-cols-2 gap-3 mb-2">
              <Input value={content.guarantees_en[index]} onChange={(e) => updateGuarantee('en', index, e.target.value)} placeholder={`Guarantee ${index + 1} (EN)`} />
              <Input value={content.guarantees_bg[index]} onChange={(e) => updateGuarantee('bg', index, e.target.value)} placeholder={`Guarantee ${index + 1} (BG)`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CTA */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Call to Action Section</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>CTA Title (EN)</Label>
              <Input value={content.cta_title_en} onChange={(e) => setContent({ ...content, cta_title_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>CTA Title (BG)</Label>
              <Input value={content.cta_title_bg} onChange={(e) => setContent({ ...content, cta_title_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>CTA Text (EN)</Label>
              <Textarea value={content.cta_text_en} onChange={(e) => setContent({ ...content, cta_text_en: e.target.value })} className="mt-1" rows={2} />
            </div>
            <div>
              <Label>CTA Text (BG)</Label>
              <Textarea value={content.cta_text_bg} onChange={(e) => setContent({ ...content, cta_text_bg: e.target.value })} className="mt-1" rows={2} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Get Quote Button (EN)</Label>
              <Input value={content.get_quote_btn_en || 'Get a Free Quote'} onChange={(e) => setContent({ ...content, get_quote_btn_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Get Quote Button (BG)</Label>
              <Input value={content.get_quote_btn_bg || 'Безплатна оферта'} onChange={(e) => setContent({ ...content, get_quote_btn_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Contact Us Button (EN)</Label>
              <Input value={content.contact_btn_en || 'Contact Us'} onChange={(e) => setContent({ ...content, contact_btn_en: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Contact Us Button (BG)</Label>
              <Input value={content.contact_btn_bg || 'Свържете се с нас'} onChange={(e) => setContent({ ...content, contact_btn_bg: e.target.value })} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid Items - 4 feature boxes */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#C41E3A]" /> Feature Grid (4 boxes next to guarantees)
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {(content.grid_items || [
              { title_en: 'EU Wide', title_bg: 'EU покритие', sub_en: 'Coverage', sub_bg: 'Широко' },
              { title_en: 'ECO', title_bg: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот' },
              { title_en: 'Secure', title_bg: 'Сигурен', sub_en: 'Transport', sub_bg: 'Транспорт' },
              { title_en: 'Expert', title_bg: 'Експертен', sub_en: 'Team', sub_bg: 'Екип' }
            ]).map((item, index) => (
              <div key={index} className="p-3 border rounded-xl bg-slate-50">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <Label className="text-xs">Title (EN)</Label>
                    <Input 
                      value={item.title_en} 
                      onChange={(e) => {
                        const newItems = [...(content.grid_items || [
                          { title_en: 'EU Wide', title_bg: 'EU покритие', sub_en: 'Coverage', sub_bg: 'Широко' },
                          { title_en: 'ECO', title_bg: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот' },
                          { title_en: 'Secure', title_bg: 'Сигурен', sub_en: 'Transport', sub_bg: 'Транспорт' },
                          { title_en: 'Expert', title_bg: 'Експертен', sub_en: 'Team', sub_bg: 'Екип' }
                        ])];
                        newItems[index] = { ...newItems[index], title_en: e.target.value };
                        setContent({ ...content, grid_items: newItems });
                      }} 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Title (BG)</Label>
                    <Input 
                      value={item.title_bg} 
                      onChange={(e) => {
                        const newItems = [...(content.grid_items || [
                          { title_en: 'EU Wide', title_bg: 'EU покритие', sub_en: 'Coverage', sub_bg: 'Широко' },
                          { title_en: 'ECO', title_bg: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот' },
                          { title_en: 'Secure', title_bg: 'Сигурен', sub_en: 'Transport', sub_bg: 'Транспорт' },
                          { title_en: 'Expert', title_bg: 'Експертен', sub_en: 'Team', sub_bg: 'Екип' }
                        ])];
                        newItems[index] = { ...newItems[index], title_bg: e.target.value };
                        setContent({ ...content, grid_items: newItems });
                      }} 
                      className="mt-1" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Subtitle (EN)</Label>
                    <Input 
                      value={item.sub_en} 
                      onChange={(e) => {
                        const newItems = [...(content.grid_items || [
                          { title_en: 'EU Wide', title_bg: 'EU покритие', sub_en: 'Coverage', sub_bg: 'Широко' },
                          { title_en: 'ECO', title_bg: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот' },
                          { title_en: 'Secure', title_bg: 'Сигурен', sub_en: 'Transport', sub_bg: 'Транспорт' },
                          { title_en: 'Expert', title_bg: 'Експертен', sub_en: 'Team', sub_bg: 'Екип' }
                        ])];
                        newItems[index] = { ...newItems[index], sub_en: e.target.value };
                        setContent({ ...content, grid_items: newItems });
                      }} 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Subtitle (BG)</Label>
                    <Input 
                      value={item.sub_bg} 
                      onChange={(e) => {
                        const newItems = [...(content.grid_items || [
                          { title_en: 'EU Wide', title_bg: 'EU покритие', sub_en: 'Coverage', sub_bg: 'Широко' },
                          { title_en: 'ECO', title_bg: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот' },
                          { title_en: 'Secure', title_bg: 'Сигурен', sub_en: 'Transport', sub_bg: 'Транспорт' },
                          { title_en: 'Expert', title_bg: 'Експертен', sub_en: 'Team', sub_bg: 'Екип' }
                        ])];
                        newItems[index] = { ...newItems[index], sub_bg: e.target.value };
                        setContent({ ...content, grid_items: newItems });
                      }} 
                      className="mt-1" 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
        <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Why Choose Us Page'}
      </Button>
    </div>
  );
}

// Settings Manager Component
function SettingsManager() {
  const [settings, setSettings] = useState({
    notification_email: '',
    company_email: '',
    company_phone: '',
    company_phone_2: '',
    address_1_name: '',
    address_1_street: '',
    address_1_city: '',
    address_2_name: '',
    address_2_street: '',
    address_2_city: '',
    address_1_maps_url: '',
    address_2_maps_url: '',
    footer_description_en: '',
    footer_description_bg: '',
    footer_facebook_url: '',
    footer_linkedin_url: '',
    footer_instagram_url: '',
    footer_copyright_en: '',
    footer_copyright_bg: '',
    send_email_notifications: true,
    // SMTP Settings
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Tetralog Ltd.',
    // Analytics
    google_analytics_id: ''
  });
  const [submissions, setSubmissions] = useState({ contacts: [], quotes: [] });
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [emailStatus, setEmailStatus] = useState({ smtp_configured: false });
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, submissionsRes, emailStatusRes, loginRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/submissions`),
        axios.get(`${API}/admin/email-status`),
        axios.get(`${API}/admin/login-attempts?limit=20`)
      ]);
      setSettings(settingsRes.data);
      // Map API response keys to expected format
      const submissionsData = submissionsRes.data;
      setSubmissions({
        contacts: submissionsData.contact_messages || submissionsData.contacts || [],
        quotes: submissionsData.quote_requests || submissionsData.quotes || []
      });
      setEmailStatus(emailStatusRes.data);
      setLoginAttempts(loginRes.data.attempts || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const clearLoginAttempts = async () => {
    if (!window.confirm('Clear all login attempt logs?')) return;
    try {
      await axios.delete(`${API}/admin/login-attempts`);
      setLoginAttempts([]);
      toast.success('Login attempts cleared');
    } catch (error) {
      toast.error('Failed to clear login attempts');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Settings saved successfully!');
      // Refresh email status after saving
      const emailStatusRes = await axios.get(`${API}/admin/email-status`);
      setEmailStatus(emailStatusRes.data);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setIsSendingTest(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`, { to_email: testEmail });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact message?')) return;
    try {
      await axios.delete(`${API}/admin/submissions/contact/${id}`);
      toast.success('Contact message deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting contact message');
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Delete this quote request?')) return;
    try {
      await axios.delete(`${API}/admin/submissions/quote/${id}`);
      toast.success('Quote request deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting quote request');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          onClick={() => setActiveTab('settings')}
          variant={activeTab === 'settings' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'settings' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <SettingsIcon className="w-4 h-4 mr-2" /> Contact Info
        </Button>
        <Button
          onClick={() => setActiveTab('email')}
          variant={activeTab === 'email' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'email' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <Mail className="w-4 h-4 mr-2" /> Email Config {emailStatus.smtp_configured ? '✓' : ''}
        </Button>
        <Button
          onClick={() => setActiveTab('analytics')}
          variant={activeTab === 'analytics' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'analytics' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <BarChart3 className="w-4 h-4 mr-2" /> Analytics {settings.google_analytics_id ? '✓' : ''}
        </Button>
        <Button
          onClick={() => setActiveTab('footer')}
          variant={activeTab === 'footer' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'footer' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <FileText className="w-4 h-4 mr-2" /> Footer
        </Button>
        <Button
          onClick={() => setActiveTab('contacts')}
          variant={activeTab === 'contacts' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'contacts' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <Inbox className="w-4 h-4 mr-2" /> Messages ({submissions.contacts?.length || 0})
        </Button>
        <Button
          onClick={() => setActiveTab('quotes')}
          variant={activeTab === 'quotes' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'quotes' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <Inbox className="w-4 h-4 mr-2" /> Quotes ({submissions.quotes?.length || 0})
        </Button>
        <Button
          onClick={() => setActiveTab('security')}
          variant={activeTab === 'security' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'security' ? 'bg-[#C41E3A] text-white' : ''}`}
        >
          <Shield className="w-4 h-4 mr-2" /> Security
        </Button>
      </div>

      {/* Security Tab - Login Attempts */}
      {activeTab === 'security' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Login Attempts</h3>
                <p className="text-sm text-slate-500">Last 20 login attempts to admin panel</p>
              </div>
              {loginAttempts.length > 0 && (
                <Button variant="outline" size="sm" onClick={clearLoginAttempts} className="text-red-500 rounded-full">
                  <Trash2 className="w-4 h-4 mr-1" /> Clear Logs
                </Button>
              )}
            </div>
            
            {loginAttempts.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No login attempts recorded yet</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {loginAttempts.map((attempt, index) => (
                  <div 
                    key={attempt.id || index} 
                    className={`p-3 rounded-xl border ${attempt.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${attempt.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className={`font-medium ${attempt.success ? 'text-green-700' : 'text-red-700'}`}>
                          {attempt.success ? '✓ Successful' : '✗ Failed'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(attempt.timestamp).toLocaleString('bg-BG')}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-600 flex flex-wrap items-center gap-2">
                      <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">{attempt.ip_address}</span>
                      {attempt.browser && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                          🌐 {attempt.browser}
                        </span>
                      )}
                      {attempt.os && (
                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">
                          💻 {attempt.os}
                        </span>
                      )}
                      {attempt.device && (
                        <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs">
                          📱 {attempt.device}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>🛡️ Rate Limiting Active:</strong> Max 5 login attempts per minute per IP address.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Configuration Tab */}
      {activeTab === 'email' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Email Configuration</h3>
            <p className="text-slate-500 text-sm mb-6">
              Configure SMTP settings to receive email notifications when someone submits a contact or quote form.
            </p>
            
            {/* Status Banner */}
            <div className={`p-4 rounded-xl mb-6 ${emailStatus.smtp_configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${emailStatus.smtp_configured ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className={`font-medium ${emailStatus.smtp_configured ? 'text-green-700' : 'text-yellow-700'}`}>
                  {emailStatus.smtp_configured ? 'Email notifications are active' : 'Email not configured - form submissions are saved but no emails sent'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* SMTP Server Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">SMTP Server</h4>
                
                <div>
                  <Label className="text-slate-700 font-medium">SMTP Host *</Label>
                  <Input
                    value={settings.smtp_host || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="e.g. smtp.gmail.com"
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Your email provider's SMTP server address
                  </p>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">SMTP Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp_port || 587}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                    placeholder="587"
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Usually 587 for TLS or 465 for SSL
                  </p>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">SMTP Username *</Label>
                  <Input
                    value={settings.smtp_user || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                    placeholder="your-email@domain.com"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">SMTP Password *</Label>
                  <Input
                    type="password"
                    value={settings.smtp_password || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                    placeholder="••••••••"
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    For Gmail, use an App Password (not your regular password)
                  </p>
                </div>
              </div>

              {/* Sender Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Sender Details</h4>
                
                <div>
                  <Label className="text-slate-700 font-medium">From Email *</Label>
                  <Input
                    type="email"
                    value={settings.smtp_from_email || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                    placeholder="noreply@tetralog.bg"
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Email address that will appear in the "From" field
                  </p>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">From Name</Label>
                  <Input
                    value={settings.smtp_from_name || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                    placeholder="Tetralog Ltd."
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Notification Email *</Label>
                  <Input
                    type="email"
                    value={settings.notification_email || ''}
                    onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                    placeholder="info@tetralog.bg"
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Where form submissions will be sent
                  </p>
                </div>

                {/* Test Email Section */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-slate-800 mb-3">Send Test Email</h4>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="rounded-xl flex-1"
                    />
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={isSendingTest || !emailStatus.smtp_configured}
                      className="bg-slate-800 text-white hover:bg-slate-700 rounded-full"
                    >
                      {isSendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                  </div>
                  {!emailStatus.smtp_configured && (
                    <p className="text-xs text-yellow-600 mt-2">
                      Save your SMTP settings first before sending a test email
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Common SMTP Providers */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-700 mb-2">Common SMTP Settings:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <strong className="text-slate-800">Gmail:</strong>
                  <p className="text-slate-500">smtp.gmail.com, Port 587</p>
                  <p className="text-xs text-slate-400">Requires App Password</p>
                </div>
                <div>
                  <strong className="text-slate-800">Outlook:</strong>
                  <p className="text-slate-500">smtp.office365.com, Port 587</p>
                </div>
                <div>
                  <strong className="text-slate-800">Yahoo:</strong>
                  <p className="text-slate-500">smtp.mail.yahoo.com, Port 587</p>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Email Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Google Analytics</h3>
            <p className="text-slate-500 text-sm mb-6">
              Configure Google Analytics to track website visitors. Analytics will only load when users accept analytics cookies.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <h4 className="font-medium text-blue-900 mb-2">How to get your Google Analytics ID:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">analytics.google.com</a></li>
                <li>Create an account or sign in</li>
                <li>Create a new property for your website</li>
                <li>Copy the Measurement ID (starts with G-)</li>
              </ol>
            </div>

            <div className="space-y-4 max-w-md">
              <div>
                <Label>Google Analytics Measurement ID</Label>
                <Input
                  placeholder="G-XXXXXXXXXX"
                  value={settings.google_analytics_id || ''}
                  onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                  className="rounded-xl mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Example: G-1234567890</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
              <h4 className="font-medium text-amber-900 mb-1">Cookie Consent Integration</h4>
              <p className="text-sm text-amber-800">
                Google Analytics will only be loaded when visitors accept "Analytics" cookies through the cookie consent banner. 
                This ensures GDPR compliance.
              </p>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Analytics Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information Settings</h3>
            <p className="text-slate-500 text-sm mb-6">
              Configure contact information displayed on the website (footer, contact page).
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Email & Phone Column */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Email & Phone</h4>

                <div>
                  <Label className="text-slate-700 font-medium">Company Email (displayed on site)</Label>
                  <Input
                    type="email"
                    value={settings.company_email}
                    onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                    placeholder="e.g. contact@tetralog.bg"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Company Phone 1</Label>
                  <Input
                    type="tel"
                    value={settings.company_phone}
                    onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                    placeholder="e.g. +359 888 123 456"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Company Phone 2 (optional)</Label>
                  <Input
                    type="tel"
                    value={settings.company_phone_2}
                    onChange={(e) => setSettings({ ...settings, company_phone_2: e.target.value })}
                    placeholder="e.g. +359 888 654 321"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              {/* Addresses Column */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Addresses</h4>
                
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <p className="text-sm font-medium text-slate-600">Address 1 (Primary)</p>
                  <div>
                    <Label className="text-slate-600 text-sm">Company Name</Label>
                    <Input
                      value={settings.address_1_name}
                      onChange={(e) => setSettings({ ...settings, address_1_name: e.target.value })}
                      placeholder="e.g. Tetralog Ltd."
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">Street / Area</Label>
                    <Input
                      value={settings.address_1_street}
                      onChange={(e) => setSettings({ ...settings, address_1_street: e.target.value })}
                      placeholder="e.g. Industrial Zone West"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">City, Country</Label>
                    <Input
                      value={settings.address_1_city}
                      onChange={(e) => setSettings({ ...settings, address_1_city: e.target.value })}
                      placeholder="e.g. Sofia, Bulgaria"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">Google Maps URL</Label>
                    <Input
                      value={settings.address_1_maps_url}
                      onChange={(e) => setSettings({ ...settings, address_1_maps_url: e.target.value })}
                      placeholder="https://maps.google.com/..."
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <p className="text-sm font-medium text-slate-600">Address 2 (Optional)</p>
                  <div>
                    <Label className="text-slate-600 text-sm">Company Name</Label>
                    <Input
                      value={settings.address_2_name}
                      onChange={(e) => setSettings({ ...settings, address_2_name: e.target.value })}
                      placeholder="e.g. Tetralog Warehouse"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">Street / Area</Label>
                    <Input
                      value={settings.address_2_street}
                      onChange={(e) => setSettings({ ...settings, address_2_street: e.target.value })}
                      placeholder="e.g. Logistics Park North"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">City, Country</Label>
                    <Input
                      value={settings.address_2_city}
                      onChange={(e) => setSettings({ ...settings, address_2_city: e.target.value })}
                      placeholder="e.g. Plovdiv, Bulgaria"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm">Google Maps URL</Label>
                    <Input
                      value={settings.address_2_maps_url}
                      onChange={(e) => setSettings({ ...settings, address_2_maps_url: e.target.value })}
                      placeholder="https://maps.google.com/..."
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Footer Tab */}
      {activeTab === 'footer' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Footer Settings</h3>
            <p className="text-slate-500 text-sm mb-6">
              Customize the footer description and social media links.
            </p>
            
            <div className="space-y-6">
              {/* Footer Description */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Footer Description (English)</Label>
                  <Textarea
                    value={settings.footer_description_en}
                    onChange={(e) => setSettings({ ...settings, footer_description_en: e.target.value })}
                    placeholder="Your trusted partner for international transport..."
                    className="mt-1 rounded-xl"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Footer Description (Bulgarian)</Label>
                  <Textarea
                    value={settings.footer_description_bg}
                    onChange={(e) => setSettings({ ...settings, footer_description_bg: e.target.value })}
                    placeholder="Вашият доверен партньор за международен транспорт..."
                    className="mt-1 rounded-xl"
                    rows={3}
                  />
                </div>
              </div>

              {/* Social Media Links */}
              <div>
                <h4 className="font-medium text-slate-800 border-b pb-2 mb-4">Social Media Links</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Facebook URL</Label>
                    <Input
                      value={settings.footer_facebook_url}
                      onChange={(e) => setSettings({ ...settings, footer_facebook_url: e.target.value })}
                      placeholder="https://facebook.com/tetralog"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">LinkedIn URL</Label>
                    <Input
                      value={settings.footer_linkedin_url}
                      onChange={(e) => setSettings({ ...settings, footer_linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/company/tetralog"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Instagram URL</Label>
                    <Input
                      value={settings.footer_instagram_url}
                      onChange={(e) => setSettings({ ...settings, footer_instagram_url: e.target.value })}
                      placeholder="https://instagram.com/tetralog"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Copyright Text */}
              <div>
                <h4 className="font-medium text-slate-800 border-b pb-2 mb-4">Copyright Text</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Copyright (English)</Label>
                    <Input
                      value={settings.footer_copyright_en}
                      onChange={(e) => setSettings({ ...settings, footer_copyright_en: e.target.value })}
                      placeholder="© 2026 Tetralog Ltd. All rights reserved."
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Copyright (Bulgarian)</Label>
                    <Input
                      value={settings.footer_copyright_bg}
                      onChange={(e) => setSettings({ ...settings, footer_copyright_bg: e.target.value })}
                      placeholder="© 2026 Tetralog Ltd. Всички права запазени."
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Footer Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Messages Tab */}
      {activeTab === 'contacts' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact Form Submissions</h3>
            {submissions.contacts?.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No contact messages yet.</p>
            ) : (
              <div className="space-y-4">
                {submissions.contacts?.map((msg) => (
                  <div key={msg.id} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-900">{msg.name}</h4>
                        <p className="text-sm text-slate-500">{msg.email} • {msg.phone || 'No phone'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {new Date(msg.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteContact(msg.id)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600 p-1 h-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Subject: {msg.subject}</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quote Requests Tab */}
      {activeTab === 'quotes' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Quote Requests</h3>
            {submissions.quotes?.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No quote requests yet.</p>
            ) : (
              <div className="space-y-4">
                {submissions.quotes?.map((quote) => (
                  <div key={quote.id} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{quote.name}</h4>
                        <p className="text-sm text-slate-500">{quote.email} • {quote.phone}</p>
                        {quote.company && <p className="text-sm text-slate-500">Company: {quote.company}</p>}
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="text-right">
                          <span className="text-xs bg-[#C41E3A]/10 text-[#C41E3A] px-2 py-1 rounded-full font-medium">
                            #{quote.id?.slice(0, 8).toUpperCase()}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(quote.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteQuote(quote.id)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600 p-1 h-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-slate-400">Service:</span>
                        <p className="text-slate-700 font-medium">{quote.service_type}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Origin:</span>
                        <p className="text-slate-700">{quote.origin}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Destination:</span>
                        <p className="text-slate-700">{quote.destination}</p>
                      </div>
                      {quote.estimated_weight && (
                        <div>
                          <span className="text-slate-400">Weight:</span>
                          <p className="text-slate-700">{quote.estimated_weight}</p>
                        </div>
                      )}
                      {quote.estimated_volume && (
                        <div>
                          <span className="text-slate-400">Volume:</span>
                          <p className="text-slate-700">{quote.estimated_volume}</p>
                        </div>
                      )}
                      {quote.preferred_date && (
                        <div>
                          <span className="text-slate-400">Preferred Date:</span>
                          <p className="text-slate-700">{quote.preferred_date}</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <span className="text-slate-400 text-sm">Cargo Description:</span>
                      <p className="text-sm text-slate-700">{quote.cargo_description}</p>
                    </div>
                    {quote.additional_info && (
                      <div className="mt-2">
                        <span className="text-slate-400 text-sm">Additional Info:</span>
                        <p className="text-sm text-slate-600">{quote.additional_info}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Main Admin Dashboard
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500">Manage site content in English and Bulgarian</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="rounded-full">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="home">
          <TabsList className="bg-white rounded-2xl p-2 mb-8 flex flex-wrap gap-2 h-auto">
            <TabsTrigger value="home" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Home className="w-4 h-4 mr-1 sm:mr-2" /> Home
            </TabsTrigger>
            <TabsTrigger value="why-choose-us" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Award className="w-4 h-4 mr-1 sm:mr-2" /> Why Us
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Languages className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Site </span>Content
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" /> Services
            </TabsTrigger>
            <TabsTrigger value="fleet" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Truck className="w-4 h-4 mr-1 sm:mr-2" /> Fleet
            </TabsTrigger>
            <TabsTrigger value="destinations" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Globe className="w-4 h-4 mr-1 sm:mr-2" /> Destinations
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Clock className="w-4 h-4 mr-1 sm:mr-2" /> About Us
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <UserCircle className="w-4 h-4 mr-1 sm:mr-2" /> Contact
            </TabsTrigger>
            <TabsTrigger value="careers" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Briefcase className="w-4 h-4 mr-1 sm:mr-2" /> Careers
            </TabsTrigger>
            <TabsTrigger value="news" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Newspaper className="w-4 h-4 mr-1 sm:mr-2" /> News
            </TabsTrigger>
            <TabsTrigger value="faq" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <HelpCircle className="w-4 h-4 mr-1 sm:mr-2" /> FAQ
            </TabsTrigger>
            <TabsTrigger value="legal" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <FileText className="w-4 h-4 mr-1 sm:mr-2" /> Legal
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white px-3 py-2 text-xs sm:text-sm">
              <Inbox className="w-4 h-4 mr-1 sm:mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home">
            <HomeManager />
          </TabsContent>

          <TabsContent value="why-choose-us">
            <WhyChooseUsManager />
          </TabsContent>

          <TabsContent value="content">
            <SiteContentManager />
          </TabsContent>

          <TabsContent value="services">
            <ServicesManager />
          </TabsContent>

          <TabsContent value="fleet">
            <FleetManager />
          </TabsContent>

          <TabsContent value="destinations">
            <DestinationsManager />
          </TabsContent>

          <TabsContent value="timeline">
            <TimelineManager />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactPersonsManager />
          </TabsContent>

          <TabsContent value="careers">
            <CareersManager />
          </TabsContent>

          <TabsContent value="news">
            <NewsManager />
          </TabsContent>

          <TabsContent value="faq">
            <FAQManager />
          </TabsContent>

          <TabsContent value="legal">
            <LegalPagesManager />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
